/**
 * AdminClusterDashboard — Cluster monitoring & ambiente management (provider-agnostic)
 * 
 * Dark theme, PT-BR, CSS-only charts, auto-refresh 30s.
 * Graceful degradation: if metrics API fails, shows cached data with error banner.
 */
import { useEffect, useState } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { api } from '../api/client';
import { AlertsTab } from './cluster/AlertsTab';
import { DeploymentsTab } from './cluster/DeploymentsTab';
import { CostTab } from './cluster/CostTab';
import { ConfigsTab } from './cluster/ConfigsTab';
import { OpenClawTab } from './cluster/OpenClawTab';
import { SkeletonBar, ListSkeleton, Spinner, Button, Input, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

// ── Types ──

interface NodeMetric {
  name: string;
  dropletId?: string;
  nodeId?: string;
  cpu: number;
  memory: number;
  disk: number;
  ip?: string;
  role?: string;
  labels?: Record<string, string>;
  taints?: Array<{ key: string; value?: string; effect: string }>;
}

interface NodePoolInfo {
  id: string;
  name: string;
  size: string;
  count: number;
  autoScale: boolean;
  minNodes: number;
  maxNodes: number;
  nodes: Array<{
    id: string;
    name: string;
    status: string;
    dropletId?: string;
    nodeId?: string;
    createdAt: string;
  }>;
}

interface ClusterMetrics {
  cluster: {
    id: string;
    name: string;
    region: string;
    version: string;
    status: string;
    nodeCount: number;
    readyNodes: number;
  };
  nodePools: NodePoolInfo[];
  autoScaling: {
    enabled: boolean;
    currentNodes: number;
    minNodes: number;
    maxNodes: number;
    atCapacity: boolean;
  };
  metrics: {
    nodes: NodeMetric[];
    averageCpu: number;
    averageMemory: number;
    averageDisk: number;
  };
  monitoring: {
    grafanaUrl: string;
    prometheusInstalled: boolean;
    alertsConfigured: number;
  };
  timestamp: string;
}

interface AdminWorkspace {
  id: string;
  customer_id: string;
  name: string;
  image: string;
  plan: string;
  plan_name?: string;
  workspace_url: string;
  status: string;
  created_at: string;
  online?: boolean;
  os?: string;
  ip?: string;
  location?: string;
  provider?: string;
  workspace_id?: string;
  type?: string;
  agent_name?: string;
  agent_model?: string;
  openclaw_installed?: boolean;
  skills_count?: number;
  skills_names?: string[];
  agent_info?: {
    agent_name?: string;
    agent_provider?: string;
    agent_model?: string;
    oc_path?: string;
    skills_list?: string[];
  };
  daily_usage?: {
    active_minutes?: number;
    limit_minutes?: number;
    pct?: number;
  };
  plan_config?: {
    memory_request?: string;
    cpu_request?: string;
    memory_limit?: string;
    cpu_limit?: string;
    storage_size?: string;
    max_replicas?: number;
    daily_hours_limit?: number;
  };
}

// ── State ──

const metricsData = signal<ClusterMetrics | null>(null);
const allWorkspaces = signal<AdminWorkspace[]>([]);
const loading = signal(true);
const errorMsg = signal('');
const tab = signal<'monitoring' | 'instances' | 'pipeline' | 'plans' | 'images' | 'namespaces' | 'events' | 'security' | 'networking' | 'storage' | 'metrics-advanced' | 'notifications' | 'alerts' | 'deployments' | 'costs' | 'configs' | 'pipelines' | 'skills-setup' | 'openclaw'>('monitoring');

// ── Instances Tab State ──
const instanceFilter = signal<'all' | 'running' | 'pending' | 'stopped' | 'creating'>('all');
const instanceSearch = signal('');
const instancePlanFilter = signal<'all' | 'free' | 'pro' | 'business'>('all');
const expandedInstance = signal<string | null>(null);

// ── V3 Proxy Health ──
interface V3Health {
  status: string;
  uptime: number;
  client_ready: boolean;
  last_token_refresh: string;
}
const v3Health = signal<V3Health | null>(null);
const v3HealthError = signal(false);
const v3HealthLoading = signal(false);

// ── New Tab State ──
const namespacesData = signal<any>(null);
const namespacesLoading = signal(false);
const namespacesError = signal('');
const namespacesFilter = signal('');

const eventsData = signal<any>(null);
const eventsLoading = signal(false);
const eventsError = signal('');
const eventsFilter = signal('');
const eventsTypeFilter = signal('all');
const eventsAutoRefresh = signal(false);
let eventsInterval: ReturnType<typeof setInterval> | null = null;

const securityData = signal<any>(null);
const securityLoading = signal(false);
const securityError = signal('');
const securitySection = signal<'rbac' | 'network' | 'accounts' | 'matrix' | 'audit'>('rbac');

const networkingData = signal<any>(null);
const networkingLoading = signal(false);
const networkingError = signal('');

const storageData = signal<any>(null);
const storageLoading = signal(false);
const storageError = signal('');

const metricsAdvData = signal<any>(null);
const metricsAdvLoading = signal(false);
const metricsAdvError = signal('');
const expandedTemplate = signal<string | null>(null);
const lastUpdate = signal('');

// ── Notifications State ──
const notificationsData = signal<any[]>([]);
const notificationsUnread = signal(0);
const notificationsLoading = signal(false);
const notificationsError = signal('');
const notificationsFilter = signal<'all' | 'error' | 'warning' | 'info'>('all');
const notificationsPage = signal(1);
const notificationsPages = signal(1);
const notifChannels = signal<any[]>([]);
const notifChannelsLoading = signal(false);
let notifInterval: ReturnType<typeof setInterval> | null = null;

// ── Live Monitoring State ──
const liveEvents = signal<any[]>([]);
const livePods = signal<any[]>([]);
const liveWorkspaces = signal<any[]>([]);
const liveLoading = signal(false);
const liveAutoRefresh = signal(true);
const liveError = signal('');
const liveExpandedCards = signal<Set<string>>(new Set());
let liveInterval: ReturnType<typeof setInterval> | null = null;

// ── Pod Logs State (A2.3, A2.4) ──
const podLogsNs = signal('');
const podLogsPod = signal('');
const podLogsContent = signal('');
const podLogsLoading = signal(false);

// ── Namespace Quotas State (A1.3) ──
const quotasData = signal<any>(null);

// ── DNS State (A4.4) ──
const dnsData = signal<any>(null);
const dnsLoading = signal(false);

// ── Metrics History (A6.2) ──
const metricsHistoryPeriod = signal<'24h' | '7d' | '30d'>('24h');

// ── Data Fetching ──

async function fetchMetrics() {
  try {
    const data = await api.get<ClusterMetrics>('/admin/cluster/metrics');
    metricsData.value = data;
    lastUpdate.value = new Date().toLocaleTimeString('pt-BR');
    errorMsg.value = '';
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Erro ao carregar metricas';
    errorMsg.value = msg;
  }
}

async function fetchWorkspaces() {
  try {
    const res = await api.get<{ workspaces: AdminWorkspace[] }>('/cloud/workspaces');
    allWorkspaces.value = res.workspaces;
  } catch {
    // Silently fail — workspaces tab is secondary
  }
}

async function fetchAll() {
  await Promise.all([fetchMetrics(), fetchWorkspaces()]);
  loading.value = false;
}

async function fetchLiveEvents() {
  liveLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/live-events');
    liveEvents.value = data.events || [];
    livePods.value = data.pods || [];
    liveWorkspaces.value = data.workspaces || [];
    liveError.value = '';
  } catch (e: unknown) {
    liveError.value = e instanceof Error ? e.message : 'Erro ao carregar eventos';
  }
  liveLoading.value = false;
}

async function fetchNamespaces() {
  namespacesLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/namespaces');
    namespacesData.value = data;
    namespacesError.value = '';
  } catch (e: unknown) { namespacesError.value = e instanceof Error ? e.message : 'Erro'; }
  namespacesLoading.value = false;
}

async function fetchEvents() {
  eventsLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/events');
    eventsData.value = data;
    eventsError.value = '';
  } catch (e: unknown) { eventsError.value = e instanceof Error ? e.message : 'Erro'; }
  eventsLoading.value = false;
}

async function fetchSecurity() {
  securityLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/security');
    securityData.value = data;
    securityError.value = '';
  } catch (e: unknown) { securityError.value = e instanceof Error ? e.message : 'Erro'; }
  securityLoading.value = false;
}

async function fetchNetworking() {
  networkingLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/networking');
    networkingData.value = data;
    networkingError.value = '';
  } catch (e: unknown) { networkingError.value = e instanceof Error ? e.message : 'Erro'; }
  networkingLoading.value = false;
}

async function fetchStorage() {
  storageLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/storage');
    storageData.value = data;
    storageError.value = '';
  } catch (e: unknown) { storageError.value = e instanceof Error ? e.message : 'Erro'; }
  storageLoading.value = false;
}

async function fetchMetricsAdvanced() {
  metricsAdvLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/metrics-advanced');
    metricsAdvData.value = data;
    metricsAdvError.value = '';
  } catch (e: unknown) { metricsAdvError.value = e instanceof Error ? e.message : 'Erro'; }
  metricsAdvLoading.value = false;
}

async function fetchNotifications() {
  notificationsLoading.value = true;
  try {
    const filter = notificationsFilter.value;
    const page = notificationsPage.value;
    let url = `/admin/cluster/metrics/notifications?page=${page}&limit=20`;
    if (filter !== 'all') url += `&type=${filter}`;
    const data = await api.get<any>(url);
    notificationsData.value = data.notifications || [];
    notificationsUnread.value = data.unread || 0;
    notificationsPages.value = data.pages || 1;
    notificationsError.value = '';
  } catch (e: unknown) { notificationsError.value = e instanceof Error ? e.message : 'Erro'; }
  notificationsLoading.value = false;
}

async function fetchNotifChannels() {
  notifChannelsLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/notification-channels');
    notifChannels.value = data.channels || [];
  } catch { /* silent */ }
  notifChannelsLoading.value = false;
}

async function markNotifRead(id: string) {
  await api.post(`/admin/cluster/metrics/notifications/${id}/read`);
  await fetchNotifications();
}

async function markAllRead() {
  await api.post('/admin/cluster/metrics/notifications/read-all');
  await fetchNotifications();
}

async function saveChannel(channelType: string, enabled: boolean, config: Record<string, unknown>) {
  await api.post('/admin/cluster/metrics/notification-channels', { channel_type: channelType, enabled, config });
  await fetchNotifChannels();
}

async function testChannel(id: string) {
  try {
    await api.post(`/admin/cluster/metrics/notification-channels/${id}/test`);
    alert('Notificacao de teste enviada!');
  } catch (e: unknown) {
    alert('Erro ao enviar teste: ' + (e instanceof Error ? e.message : 'Erro'));
  }
}

async function deleteChannel(id: string) {
  if (!confirm('Remover este canal?')) return;
  await api.delete(`/admin/cluster/metrics/notification-channels/${id}`);
  await fetchNotifChannels();
}

async function fetchPodLogs(ns: string, pod: string) {
  podLogsLoading.value = true;
  try {
    const resp = await fetch(`/api/v1/admin/cluster/metrics/pods/${ns}/${pod}/logs?tail=500`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    podLogsContent.value = await resp.text();
  } catch { podLogsContent.value = 'Erro ao carregar logs'; }
  podLogsLoading.value = false;
}

function downloadLogs() {
  const blob = new Blob([podLogsContent.value], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${podLogsPod.value || 'pod'}-logs.txt`;
  a.click(); URL.revokeObjectURL(url);
}

async function fetchQuotas() {
  try {
    const data = await api.get<any>('/admin/cluster/metrics/namespaces/quotas');
    quotasData.value = data;
  } catch { /* silent */ }
}

async function fetchDns() {
  dnsLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/dns');
    dnsData.value = data;
  } catch { /* silent */ }
  dnsLoading.value = false;
}

async function createNamespace() {
  const name = prompt('Nome do namespace:');
  if (!name) return;
  try {
    await api.post('/admin/cluster/metrics/namespaces', { name });
    alert('Namespace criado!');
    fetchNamespaces();
  } catch (e: unknown) { alert('Erro: ' + (e instanceof Error ? e.message : 'Erro')); }
}

async function deleteNamespace(name: string) {
  if (!confirm(`Deletar namespace ${name}? ATENCAO: Isso remove TODOS os recursos dentro dele!`)) return;
  try {
    await api.delete(`/admin/cluster/metrics/namespaces/${name}`);
    alert('Namespace deletado!');
    fetchNamespaces();
  } catch (e: unknown) { alert('Erro: ' + (e instanceof Error ? e.message : 'Erro')); }
}

function exportMetricsCsv() {
  const data = metricsAdvData.value;
  if (!data) return;
  let csv = 'Namespace,Pods,CPU (m),RAM (Mi),Custo Est. (USD)\n';
  for (const ns of (data.byNamespace || [])) {
    csv += `${ns.namespace},${ns.pods},${Math.round(ns.cpu)},${Math.round(ns.memory)},${(ns.cpu * 0.02 + ns.memory * 0.005).toFixed(2)}\n`;
  }
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'cluster-metrics.csv';
  a.click(); URL.revokeObjectURL(url);
}

async function adminRestart(id: string) {
  await api.post(`/cloud/workspaces/${id}/restart`);
  setTimeout(fetchAll, 3000);
}

async function adminDelete(id: string) {
  if (!confirm(`Deletar ambiente ${id}?`)) return;
  await api.delete(`/cloud/workspaces/${id}`);
  await fetchAll();
}

async function adminScale(id: string) {
  const replicas = prompt('Numero de replicas (0-10):');
  if (!replicas) return;
  await api.patch(`/cloud/workspaces/${id}/scale`, { replicas: parseInt(replicas) });
  alert(`Scaled to ${replicas}`);
}

// ── Styles ──

const colors = {
  bg: T.bg,
  card: `${T.border}`,
  cardBorder: T.borderHover,
  text: T.text,
  textMuted: T.textSecondary,
  green: T.success,
  red: T.danger,
  yellow: T.warning,
  blue: T.primary,
  purple: T.purple,
};

const STATUS_COLORS: Record<string, string> = {
  running: colors.green,
  creating: colors.yellow,
  pending: colors.yellow,
  restarting: colors.blue,
  failed: colors.red,
  deleted: colors.textMuted,
};

// ── SVG Icons ──

function IconServer() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
      <line x1="6" y1="6" x2="6.01" y2="6" />
      <line x1="6" y1="18" x2="6.01" y2="18" />
    </svg>
  );
}

function IconCpu() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" />
    </svg>
  );
}

function IconMemory() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M6 19v-8a6 6 0 0 1 12 0v8" /><rect x="4" y="19" width="16" height="2" rx="1" />
      <line x1="8" y1="11" x2="8" y2="15" /><line x1="12" y1="11" x2="12" y2="15" />
      <line x1="16" y1="11" x2="16" y2="15" />
    </svg>
  );
}

function IconDisk() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconScale() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function IconChart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function IconAlert() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconExternalLink() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// ── Components ──

function MetricBar({ label, value, max, color, icon }: {
  label: string;
  value: number;
  max?: number;
  color: string;
  icon?: preact.ComponentChildren;
}) {
  const pct = Math.min(100, Math.max(0, max ? (value / max) * 100 : value));
  const barColor = pct > 90 ? colors.red : pct > 70 ? colors.yellow : color;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.textMuted, fontSize: 13 }}>
          {icon}
          <span>{label}</span>
        </div>
        <span style={{ color: barColor, fontWeight: 600, fontSize: 14 }}>
          {max ? `${value}/${max}` : `${value.toFixed(1)}%`}
        </span>
      </div>
      <div style={{ width: '100%', height: 8, background: T.borderHover, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: `linear-gradient(90deg, ${barColor}, ${barColor}dd)`,
          borderRadius: 4,
          transition: 'width 0.5s ease',
        }} />
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, icon }: {
  label: string;
  value: string;
  color: string;
  icon: preact.ComponentChildren;
}) {
  return (
    <div style={{
      background: colors.card,
      borderRadius: 12,
      padding: '14px 12px',
      border: `1px solid ${colors.cardBorder}`,
      textAlign: 'center',
    }}>
      <div style={{ color, marginBottom: 6, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function NodeCard({ node, metric }: { node: { name: string; status: string }; metric?: NodeMetric }) {
  const isReady = node.status === 'running';
  return (
    <div style={{
      background: colors.card,
      borderRadius: 10,
      padding: 16,
      border: `1px solid ${colors.cardBorder}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ color: colors.text, fontWeight: 600, fontSize: 14 }}>
          {node.name.replace('pool-workers-', 'worker-')}
        </span>
        <span style={{
          padding: '3px 10px',
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 600,
          color: T.text,
          background: isReady ? colors.green : colors.red,
        }}>
          {isReady ? 'Ready' : 'NotReady'}
        </span>
      </div>
      {metric && (
        <>
          <MetricBar label="CPU" value={metric.cpu} color={colors.blue} icon={<IconCpu />} />
          <MetricBar label="RAM" value={metric.memory} color={colors.purple} icon={<IconMemory />} />
          <MetricBar label="Disco" value={metric.disk} color={colors.yellow} icon={<IconDisk />} />
        </>
      )}
    </div>
  );
}

// ── Main Component ──

// ── Live Environments Section ──

function LiveEnvironmentsSection() {
  useEffect(() => {
    fetchLiveEvents();
    if (liveAutoRefresh.value) {
      liveInterval = setInterval(fetchLiveEvents, 5000);
    }
    return () => { if (liveInterval) clearInterval(liveInterval); };
  }, []);

  useEffect(() => {
    if (liveInterval) clearInterval(liveInterval);
    if (liveAutoRefresh.value) {
      liveInterval = setInterval(fetchLiveEvents, 5000);
    }
    return () => { if (liveInterval) clearInterval(liveInterval); };
  }, [liveAutoRefresh.value]);

  const toggleExpand = (ns: string) => {
    const s = new Set(liveExpandedCards.value);
    if (s.has(ns)) s.delete(ns); else s.add(ns);
    liveExpandedCards.value = s;
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'running': return '';
      case 'creating': case 'provisioning': return '';
      case 'failed': case 'error': return '';
      case 'stopping': case 'stopped': return '';
      default: return '';
    }
  };

  const getEventColor = (type: string, reason: string) => {
    if (type === 'Warning') {
      if (['OOMKilled', 'CrashLoopBackOff'].includes(reason)) return T.danger;
      return T.warning;
    }
    if (['Pulling', 'Waiting'].includes(reason)) return T.primary;
    return T.success;
  };

  const getEventIcon = (type: string, reason: string) => {
    if (type === 'Warning') return '';
    if (['Pulling', 'Waiting'].includes(reason)) return '';
    if (['Started', 'Running'].includes(reason)) return '';
    return '●';
  };

  const formatTime = (ts: string) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return ts; }
  };

  const formatUptime = (startedAt: string) => {
    if (!startedAt) return '';
    const diff = Date.now() - new Date(startedAt).getTime();
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Group events by namespace
  const eventsByNs: Record<string, any[]> = {};
  for (const ev of liveEvents.value) {
    if (!eventsByNs[ev.namespace]) eventsByNs[ev.namespace] = [];
    eventsByNs[ev.namespace].push(ev);
  }

  // Build environment cards: merge workspaces + pods + events
  const envCards = liveWorkspaces.value.map((ws: any) => {
    const ns = `ws-${ws.id}`;
    const pod = livePods.value.find((p: any) => p.namespace === ns);
    const events = (eventsByNs[ns] || []).sort((a: any, b: any) => a.lastTimestamp.localeCompare(b.lastTimestamp));
    const warnings = events.filter((e: any) => e.type === 'Warning').length;
    return { ws, pod, events, ns, warnings };
  });

  // Also add orphan pods (pods without workspace record)
  const wsNamespaces = new Set(liveWorkspaces.value.map((ws: any) => `ws-${ws.id}`));
  const orphanPods = livePods.value.filter((p: any) => !wsNamespaces.has(p.namespace));
  for (const pod of orphanPods) {
    const events = (eventsByNs[pod.namespace] || []).sort((a: any, b: any) => a.lastTimestamp.localeCompare(b.lastTimestamp));
    const warnings = events.filter((e: any) => e.type === 'Warning').length;
    envCards.push({ ws: { id: pod.namespace.replace('ws-', ''), name: pod.namespace, status: pod.phase?.toLowerCase() || 'unknown', image: pod.image }, pod, events, ns: pod.namespace, warnings });
  }

  return (
    <div style={{ marginTop: 24 }}>
      <style>{`
        @keyframes livePulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } }
        @keyframes liveFadeIn { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes liveSpin { to { transform: rotate(360deg) } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, margin: 0 }}>
            Ambientes em Tempo Real
          </h3>
          {liveAutoRefresh.value && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.successBg, padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: T.success }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.success, animation: 'livePulse 1.5s ease-in-out infinite' }} />
              LIVE
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={() => { liveAutoRefresh.value = !liveAutoRefresh.value; }}>
            {liveAutoRefresh.value ? '● Auto 5s' : '○ Parado'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => fetchLiveEvents()}>
            ⟳ Refresh
          </Button>
        </div>
      </div>

      {liveError.value && (
        <div style={{ background: T.dangerBg, border: '1px solid #f85149', borderRadius: 8, padding: 12, marginBottom: 12, color: T.danger, fontSize: 13 }}>
          {liveError.value}
        </div>
      )}

      {liveLoading.value && envCards.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>
          <span style={{ display: 'inline-block', width: 20, height: 20, border: '2px solid #58a6ff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'liveSpin 0.8s linear infinite' }} />
          <p style={{ marginTop: 8 }}><ListSkeleton rows={3} /></p>
        </div>
      )}

      {!liveLoading.value && envCards.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}` }}>
          <span style={{ fontSize: 32 }}></span>
          <p style={{ marginTop: 8 }}>Nenhum ambiente ativo no momento</p>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {envCards.map(({ ws, pod, events, ns, warnings }) => {
          const isExpanded = liveExpandedCards.value.has(ns);
          const isCreating = ['creating', 'provisioning'].includes(ws.status);
          return (
            <div key={ns} style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'hidden', animation: 'liveFadeIn 0.3s ease-out' }}>
              {/* Card Header */}
              <div
                onClick={() => toggleExpand(ns)}
                style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isExpanded ? `1px solid ${colors.cardBorder}` : 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16 }}>{getStatusEmoji(ws.status)}</span>
                  <div>
                    <span style={{ color: colors.text, fontWeight: 600, fontSize: 14 }}>{ws.name}</span>
                    {isCreating && (
                      <span style={{ marginLeft: 8, display: 'inline-block', width: 12, height: 12, border: '2px solid #d29922', borderTopColor: 'transparent', borderRadius: '50%', animation: 'liveSpin 0.8s linear infinite', verticalAlign: 'middle' }} />
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {warnings > 0 && (
                    <span style={{ background: T.warningBg, color: T.warning, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                      {warnings}
                    </span>
                  )}
                  {pod && (
                    <span style={{ fontSize: 11, color: colors.textMuted, background: T.bgCardHover, padding: '2px 8px', borderRadius: 6 }}>
                      {pod.phase}
                    </span>
                  )}
                  <span style={{ color: colors.textMuted, fontSize: 14, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div style={{ padding: 16 }}>
                  {/* Pod Info */}
                  {pod && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 16, fontSize: 13 }}>
                      <div><span style={{ color: colors.textMuted }}>Status: </span><span style={{ color: colors.text }}>{getStatusEmoji(ws.status)} {ws.status}</span></div>
                      <div><span style={{ color: colors.textMuted }}>Pod: </span><span style={{ color: colors.text }}>{pod.phase}</span></div>
                      <div><span style={{ color: colors.textMuted }}>Node: </span><span style={{ color: colors.text }}>{pod.nodeName || '-'}</span></div>
                      <div><span style={{ color: colors.textMuted }}>Restarts: </span><span style={{ color: pod.restartCount > 0 ? T.danger : colors.text }}>{pod.restartCount}</span></div>
                      {pod.startedAt && <div><span style={{ color: colors.textMuted }}>Uptime: </span><span style={{ color: colors.text }}>{formatUptime(pod.startedAt)}</span></div>}
                      {pod.reason && <div><span style={{ color: colors.textMuted }}>Razão: </span><span style={{ color: T.danger }}>{pod.reason}</span></div>}
                    </div>
                  )}

                  {/* Timeline */}
                  <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8, fontWeight: 600 }}>Timeline</div>
                  {events.length === 0 && (
                    <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>Nenhum evento recente</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 300, overflowY: 'auto' }}>
                    {events.map((ev: any, i: number) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, lineHeight: 1.5 }}>
                        <span style={{ color: getEventColor(ev.type, ev.reason), flexShrink: 0 }}>{getEventIcon(ev.type, ev.reason)}</span>
                        <span style={{ color: T.primary, flexShrink: 0, fontFamily: 'monospace', fontSize: 11 }}>{formatTime(ev.lastTimestamp)}</span>
                        <span style={{ color: getEventColor(ev.type, ev.reason), fontWeight: 600, flexShrink: 0 }}>{ev.reason}</span>
                        <span style={{ color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.message}</span>
                      </div>
                    ))}
                  </div>

                  {/* Image info */}
                  {pod?.image && (
                    <div style={{ marginTop: 12, fontSize: 11, color: colors.textMuted, fontFamily: 'monospace', background: T.bgCard, padding: '6px 10px', borderRadius: 6 }}>
                      {pod.image}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminClusterDashboard() {
  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

  const m = metricsData.value;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', background: colors.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: colors.text, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconServer />
            Cluster Dashboard
          </h1>
          <p style={{ fontSize: 12, color: colors.textMuted, margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
            {m ? (
              <>
                <span style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                  background: m.cluster.name?.toLowerCase().includes('k3s') ? T.successBg : T.primaryBg,
                  color: m.cluster.name?.toLowerCase().includes('k3s') ? colors.green : colors.blue,
                  border: `1px solid ${m.cluster.name?.toLowerCase().includes('k3s') ? T.successBg : T.primaryBg}`,
                }}>
                  {m.cluster.name?.toLowerCase().includes('k3s') ? 'K3s' : m.cluster.name?.toLowerCase().includes('doks') ? 'DOKS' : 'K8s'}
                </span>
                {`${m.cluster.name} | ${m.cluster.region.toUpperCase()} | v${m.cluster.version}`}
              </>
            ) : <SkeletonBar width={80} height={14} style={{ display: 'inline-block' }} />}
            {lastUpdate.value && ` | Atualizado: ${lastUpdate.value}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {m?.monitoring.grafanaUrl && (
            <a
              href={m.monitoring.grafanaUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '8px 16px', background: colors.blue, color: T.text,
                border: 'none', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600,
              }}
            >
              <IconChart />
              Grafana
              <IconExternalLink />
            </a>
          )}
        </div>
      </div>

      {/* Error banner — esconder em abas independentes (OpenClaw, etc) que não dependem de métricas */}
      {errorMsg.value && tab.value !== 'openclaw' && (
        <div style={{
          padding: 12, background: T.dangerBg, borderRadius: 8,
          color: colors.red, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <IconAlert />
          {errorMsg.value}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4, scrollbarWidth: 'none' }}>
        {([
          { key: 'monitoring' as const, label: 'Monitoramento' },
          { key: 'instances' as const, label: 'Instancias' },
          { key: 'pipeline' as const, label: 'Pipeline K8s' },
          { key: 'plans' as const, label: 'Planos' },
          { key: 'images' as const, label: 'Imagens Docker' },
          { key: 'namespaces' as const, label: 'Namespaces' },
          { key: 'events' as const, label: 'Events' },
          { key: 'security' as const, label: 'Segurança' },
          { key: 'networking' as const, label: 'Networking' },
          { key: 'storage' as const, label: 'Storage' },
          { key: 'metrics-advanced' as const, label: 'Metricas' },
          { key: 'notifications' as const, label: 'Notificacoes' },
          { key: 'alerts' as const, label: 'Alertas' },
          { key: 'deployments' as const, label: 'Deployments' },
          { key: 'costs' as const, label: 'Custos/ROI' },
          { key: 'configs' as const, label: 'Configs/YAML' },
          { key: 'pipelines' as const, label: 'Pipelines' },
          { key: 'skills-setup' as const, label: 'Skills Setup' },
          { key: 'openclaw' as const, label: 'OpenClaw' },
        ]).map(t => (
          <Button variant="ghost" onClick={() => tab.value = t.key}>
            {t.label}
          </Button>
        ))}
      </div>

      {/* Monitoring Tab */}
      {tab.value === 'monitoring' && m && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
            <KpiCard
              label="Nodes Ready"
              value={`${m.cluster.readyNodes}/${m.cluster.nodeCount}`}
              color={m.cluster.readyNodes === m.cluster.nodeCount ? colors.green : colors.red}
              icon={<IconServer />}
            />
            <KpiCard
              label="CPU Media"
              value={`${m.metrics.averageCpu}%`}
              color={m.metrics.averageCpu > 85 ? colors.red : m.metrics.averageCpu > 70 ? colors.yellow : colors.blue}
              icon={<IconCpu />}
            />
            <KpiCard
              label="RAM Media"
              value={`${m.metrics.averageMemory}%`}
              color={m.metrics.averageMemory > 90 ? colors.red : m.metrics.averageMemory > 75 ? colors.yellow : colors.purple}
              icon={<IconMemory />}
            />
            <KpiCard
              label="Disco Medio"
              value={`${m.metrics.averageDisk}%`}
              color={m.metrics.averageDisk > 85 ? colors.red : colors.yellow}
              icon={<IconDisk />}
            />
            <KpiCard
              label="Alertas Ativos"
              value={String(m.monitoring.alertsConfigured)}
              color={colors.green}
              icon={<IconAlert />}
            />
          </div>

          {/* Scaling Status — Provider-aware */}
          {(() => {
            const isK3s = m.cluster.name?.toLowerCase().includes('k3s');
            return (
              <div style={{
                background: colors.card, borderRadius: 12, padding: 20,
                border: `1px solid ${colors.cardBorder}`, marginBottom: 24,
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconScale />
                  {isK3s ? 'Escala Manual (K3s)' : 'Auto-Scaling'}
                </h3>
                {isK3s ? (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>Nodes Ativos</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: colors.green }}>{m.cluster.readyNodes}/{m.cluster.nodeCount}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>Provider</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: colors.blue }}>OVH Bare Metal</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>Modo</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: colors.yellow }}>Manual</div>
                      </div>
                    </div>
                    <div style={{ padding: 12, background: T.primaryBg, borderRadius: 8, border: `1px solid ${T.primaryBg}` }}>
                      <div style={{ fontSize: 13, color: colors.text, fontWeight: 600, marginBottom: 6 }}>➕ Adicionar Worker</div>
                      <div style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.6 }}>
                        1. Comprar servidor ADV-1 v2 na OVH (€148/mes, 128GB RAM)<br />
                        2. Instalar Ubuntu 22.04 LTS<br />
                        3. Executar: <code style={{ background: T.borderHover, padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
                          curl -sfL https://get.k3s.io | K3S_URL=https://51.210.192.229:6443 K3S_TOKEN=&lt;token&gt; sh -
                        </code>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>Status</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: m.autoScaling?.enabled ? colors.green : colors.red }}>
                          {m.autoScaling?.enabled ? 'Ativo' : 'Desativado'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>Nodes Atuais</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: colors.text }}>{m.autoScaling?.currentNodes || m.cluster.nodeCount}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>Minimo</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: colors.text }}>{m.autoScaling?.minNodes || 1}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>Maximo</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: colors.text }}>{m.autoScaling?.maxNodes || m.cluster.nodeCount}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <MetricBar
                        label="Utilizacao de Nodes"
                        value={m.autoScaling?.currentNodes || m.cluster.nodeCount}
                        max={m.autoScaling?.maxNodes || m.cluster.nodeCount}
                        color={colors.blue}
                        icon={<IconServer />}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Nodes Table (Provider-Agnostic) ── */}
          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconServer />
            Nodes ({m.cluster.readyNodes}/{m.cluster.nodeCount})
          </h3>

          {/* Detailed nodes table for K3s, card grid for DOKS */}
          {(() => {
            const isK3s = m.cluster.name?.toLowerCase().includes('k3s');
            const allNodes = m.metrics.nodes.length > 0 ? m.metrics.nodes : m.nodePools.flatMap(p => p.nodes.map(n => ({ name: n.name, cpu: 0, memory: 0, disk: 0 } as NodeMetric)));

            if (isK3s && allNodes.length > 0) {
              return (
                <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'hidden', marginBottom: 24 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                        {['Hostname', 'IP', 'Role', 'Status', 'CPU%', 'RAM%', 'Disco%'].map(h => (
                          <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allNodes.map((node, i) => {
                        const role = node.role || (node.labels?.['node-role.kubernetes.io/master'] !== undefined ? 'master' : node.labels?.['node-role.kubernetes.io/control-plane'] !== undefined ? 'master' : 'worker');
                        const isReady = true; // nodes in metrics are ready
                        return (
                          <tr key={node.name} style={{ borderBottom: i < allNodes.length - 1 ? `1px solid ${colors.cardBorder}` : 'none' }}>
                            <td style={{ padding: '10px 16px', color: colors.text, fontWeight: 600 }}>{node.name}</td>
                            <td style={{ padding: '10px 16px', color: colors.textMuted, fontFamily: 'monospace', fontSize: 12 }}>{node.ip || '—'}</td>
                            <td style={{ padding: '10px 16px' }}>
                              <span style={{
                                padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                background: role === 'master' ? T.purpleBg : T.primaryBg,
                                color: role === 'master' ? colors.purple : colors.blue,
                              }}>{role.toUpperCase()}</span>
                            </td>
                            <td style={{ padding: '10px 16px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isReady ? colors.green : colors.red }} />
                                <span style={{ color: isReady ? colors.green : colors.red, fontSize: 12, fontWeight: 600 }}>{isReady ? 'Ready' : 'NotReady'}</span>
                              </span>
                            </td>
                            <td style={{ padding: '10px 16px', color: node.cpu > 80 ? colors.red : node.cpu > 60 ? colors.yellow : colors.green, fontWeight: 600 }}>{node.cpu}%</td>
                            <td style={{ padding: '10px 16px', color: node.memory > 85 ? colors.red : node.memory > 70 ? colors.yellow : colors.green, fontWeight: 600 }}>{node.memory}%</td>
                            <td style={{ padding: '10px 16px', color: node.disk > 85 ? colors.red : node.disk > 70 ? colors.yellow : colors.green, fontWeight: 600 }}>{node.disk}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }

            // Fallback: card grid (DOKS or generic)
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 24 }}>
                {m.nodePools.flatMap(pool =>
                  pool.nodes.map(node => {
                    const metric = m.metrics.nodes.find(n => (n.dropletId && n.dropletId === node.dropletId) || (n.nodeId && n.nodeId === node.nodeId) || n.name === node.name);
                    return <NodeCard key={node.id} node={node} metric={metric} />;
                  }),
                )}
              </div>
            );
          })()}

          {/* ── Componentes da Infraestrutura (K3s) ── */}
          {m.cluster.name?.toLowerCase().includes('k3s') && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                Componentes da Infraestrutura
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                {[
                  { name: 'K3s', version: m.cluster.version || 'v1.34.5+k3s1', status: 'Ativo', color: colors.green, desc: `${m.cluster.readyNodes} nodes prontos` },
                  { name: 'Longhorn', version: 'v1.8.1', status: 'Ativo', color: colors.green, desc: 'Storage distribuido' },
                  { name: 'NGINX Ingress', version: 'v1.12.2', status: 'Ativo', color: colors.green, desc: 'Controlador de ingress' },
                  { name: 'cert-manager', version: 'v1.17.2', status: 'Ativo', color: colors.green, desc: "Let's Encrypt TLS" },
                  { name: 'k8s-proxy', version: 'latest', status: 'Ativo', color: colors.green, desc: 'k8s-proxy.yourdomain.com' },
                ].map(comp => (
                  <div key={comp.name} style={{
                    background: colors.card, borderRadius: 10, padding: 16,
                    border: `1px solid ${colors.cardBorder}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>{comp.name}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                        background: `${comp.color}22`, color: comp.color,
                      }}>{comp.status}</span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Versao: <span style={{ color: colors.text, fontFamily: 'monospace' }}>{comp.version}</span></div>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>{comp.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monitoring Info */}
          <div style={{
            background: colors.card, borderRadius: 12, padding: 20,
            border: `1px solid ${colors.cardBorder}`,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconChart />
              Stack de Monitoramento
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.monitoring.prometheusInstalled ? colors.green : colors.red }} />
                <span style={{ color: colors.text, fontSize: 14 }}>Prometheus</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors.green }} />
                <span style={{ color: colors.text, fontSize: 14 }}>Grafana</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors.green }} />
                <span style={{ color: colors.text, fontSize: 14 }}>AlertManager ({m.monitoring.alertsConfigured} alertas)</span>
              </div>
            </div>
          </div>

          {/* ── Ambientes em Tempo Real ── */}
          <LiveEnvironmentsSection />
        </>
      )}

      {/* Instances Tab */}
      {tab.value === 'instances' && <InstancesTab />}

      {/* Pipeline Tab */}
      {tab.value === 'pipeline' && <PipelineTab />}

      {/* Plans Tab */}
      {tab.value === 'plans' && <PlansTab />}

      {/* Images Tab */}
      {tab.value === 'images' && <ImagesTab />}

      {/* Namespaces Tab */}
      {tab.value === 'namespaces' && <NamespacesTab />}

      {/* Events Tab */}
      {tab.value === 'events' && <EventsTab />}

      {/* Security Tab */}
      {tab.value === 'security' && <SecurityTab />}

      {/* Networking Tab */}
      {tab.value === 'networking' && <NetworkingTab />}

      {/* Storage Tab */}
      {tab.value === 'storage' && <StorageTab />}

      {/* Metrics Advanced Tab */}
      {tab.value === 'metrics-advanced' && <MetricsAdvancedTab />}

      {/* Notifications Tab */}
      {tab.value === 'notifications' && <NotificationsTab />}

      {/* B1: Alerts Tab */}
      {tab.value === 'alerts' && <AlertsTab />}

      {/* B2: Deployments Tab */}
      {tab.value === 'deployments' && <DeploymentsTab />}

      {/* B3: Costs Tab */}
      {tab.value === 'costs' && <CostTab />}

      {/* B4: Configs Tab */}
      {tab.value === 'configs' && <ConfigsTab />}
      {tab.value === 'pipelines' && <PipelinesRegistryTab />}
      {tab.value === 'skills-setup' && <SkillsSetupTab />}
      {tab.value === 'openclaw' && <OpenClawTab />}
    </div>
  );
}

// ── K8s Pipeline Data ──

interface K8sTemplate {
  file: string;
  kind: string;
  apiVersion: string;
  icon: string;
  step: number;
  name: string;
  desc: string;
  variables: string[];
  keyConfig: Record<string, unknown>;
}

const K8S_TEMPLATES: K8sTemplate[] = [
  {
    file: 'namespace-customer-template.yaml', kind: 'Namespace', apiVersion: 'v1', icon: '', step: 1,
    name: 'Namespace do Cliente', desc: 'Isolamento completo por cliente — cada customer tem seu namespace',
    variables: ['CUSTOMER_ID', 'PLAN'],
    keyConfig: { namePattern: 'app-customer-{id}', labels: ['managed-by', 'customer-id', 'plan'] },
  },
  {
    file: 'namespace-system.yaml', kind: 'Namespace', apiVersion: 'v1', icon: '', step: 0,
    name: 'Namespace Sistema', desc: 'Namespace do WhiteLabelClaw (serviços internos)',
    variables: [], keyConfig: { name: 'app-system' },
  },
  {
    file: 'resourcequota-plans.yaml', kind: 'ResourceQuota', apiVersion: 'v1', icon: '', step: 2,
    name: 'Resource Quota (Planos)', desc: 'Limites de CPU/RAM/Pods por plano',
    variables: ['PLAN'],
    keyConfig: {
      plans: [
        { name: 'free', pods: 1, memory: '512Mi', cpu: '250m', memLimit: '512Mi', cpuLimit: '500m' },
        { name: 'starter', pods: 2, memory: '1Gi', cpu: '500m', memLimit: '1Gi', cpuLimit: '1' },
        { name: 'pro', pods: 3, memory: '2Gi', cpu: '1', memLimit: '2Gi', cpuLimit: '2' },
        { name: 'business', pods: 5, memory: '4Gi', cpu: '2', memLimit: '4Gi', cpuLimit: '4' },
        { name: 'enterprise', pods: 10, memory: '8Gi', cpu: '4', memLimit: '8Gi', cpuLimit: '8' },
      ],
    },
  },
  {
    file: 'limitrange-default.yaml', kind: 'LimitRange', apiVersion: 'v1', icon: '', step: 2,
    name: 'Limites Default', desc: 'Limites padrão por container (CPU: 50m-500m, RAM: 64Mi-512Mi)',
    variables: [], keyConfig: { defaultCpu: '250m', defaultMemory: '256Mi', maxCpu: '500m', maxMemory: '512Mi' },
  },
  {
    file: 'networkpolicy-isolation.yaml', kind: 'NetworkPolicy', apiVersion: 'networking.k8s.io/v1', icon: '', step: 2,
    name: 'Network Isolation', desc: 'Bloqueio total entre namespaces + permite DNS',
    variables: [], keyConfig: { denyAllIngress: true, allowDNS: true, allowSameNamespace: true },
  },
  {
    file: 'configmap-workspace.yaml', kind: 'ConfigMap', apiVersion: 'v1', icon: '', step: 3,
    name: 'ConfigMap Workspace', desc: 'Configurações não-sensíveis (plan, port, env)',
    variables: ['WORKSPACE_ID', 'CUSTOMER_ID', 'NAMESPACE', 'PLAN', 'PORT'],
    keyConfig: { fields: ['WORKSPACE_ID', 'CUSTOMER_ID', 'PLAN', 'PORT', 'NODE_ENV', 'LOG_LEVEL', 'TZ'] },
  },
  {
    file: 'secret-workspace.yaml', kind: 'Secret', apiVersion: 'v1', icon: '', step: 3,
    name: 'Secret Workspace', desc: 'Credenciais criptografadas (API keys, tokens)',
    variables: ['WORKSPACE_ID', 'NAMESPACE'],
    keyConfig: { fields: ['API_KEY', 'AUTH_TOKEN', 'ENCRYPTION_KEY'], type: 'Opaque', encoding: 'base64' },
  },
  {
    file: 'pvc-workspace.yaml', kind: 'PersistentVolumeClaim', apiVersion: 'v1', icon: '', step: 4,
    name: 'Disco Persistente', desc: 'Storage que sobrevive restarts (dados do ambiente)',
    variables: ['WORKSPACE_ID', 'NAMESPACE', 'STORAGE_SIZE', 'STORAGE_CLASS'],
    keyConfig: { accessMode: 'ReadWriteOnce', defaultSize: '10Gi' },
  },
  {
    file: 'deployment-workspace.yaml', kind: 'Deployment', apiVersion: 'apps/v1', icon: '', step: 5,
    name: 'Deployment (Pod)', desc: 'Container principal do ambiente com probes e volumes',
    variables: ['WORKSPACE_ID', 'CUSTOMER_ID', 'NAMESPACE', 'IMAGE', 'PORT', 'CPU_REQUEST', 'MEMORY_REQUEST', 'CPU_LIMIT', 'MEMORY_LIMIT', 'REPLICAS'],
    keyConfig: {
      strategy: 'RollingUpdate',
      probes: ['readiness (5s)', 'liveness (15s)', 'startup (5s)'],
      volumes: ['workspace-data (PVC)', 'workspace-config (ConfigMap)'],
      antiAffinity: 'spread across nodes',
    },
  },
  {
    file: 'service-workspace.yaml', kind: 'Service', apiVersion: 'v1', icon: '', step: 6,
    name: 'Service (ClusterIP)', desc: 'Duas portas: 80 (Web UI) + 18789 (Gateway API para Chat Hub)',
    variables: ['WORKSPACE_ID', 'NAMESPACE', 'PORT'],
    keyConfig: { type: 'ClusterIP', ports: '80 (web) + 18789 (gateway)' },
  },
  {
    file: 'ingress-workspace.yaml', kind: 'Ingress', apiVersion: 'networking.k8s.io/v1', icon: '', step: 7,
    name: 'Ingress + TLS', desc: 'Domínio público + HTTPS automático (Let\'s Encrypt)',
    variables: ['WORKSPACE_ID', 'NAMESPACE', 'HOST'],
    keyConfig: { class: 'nginx', issuer: 'letsencrypt-prod', proxyBodySize: '50m', websocket: true, timeout: '3600s' },
  },
  {
    file: 'hpa-workspace.yaml', kind: 'HorizontalPodAutoscaler', apiVersion: 'autoscaling/v2', icon: '', step: 7,
    name: 'Auto-Scaling', desc: 'Escala automática baseada em CPU/RAM',
    variables: ['WORKSPACE_ID', 'NAMESPACE', 'MIN_REPLICAS', 'MAX_REPLICAS'],
    keyConfig: { targetCPU: '80%', targetMemory: '85%', scaleDown: '300s stabilization' },
  },
  {
    file: 'clusterissuer-letsencrypt.yaml', kind: 'ClusterIssuer', apiVersion: 'cert-manager.io/v1', icon: '', step: 0,
    name: 'Let\'s Encrypt Issuer', desc: 'Certificados TLS automáticos (ACME HTTP-01)',
    variables: [], keyConfig: { server: 'acme-v02.api.letsencrypt.org', solver: 'http01', class: 'nginx' },
  },
  {
    file: 'router-token-provisioning', kind: 'RouterTokenService', apiVersion: 'internal', icon: '', step: 8,
    name: 'Router Token', desc: 'Gera token de acesso ao Elon Router via RouterTokenService.createAutoToken()',
    variables: ['CUSTOMER_ID', 'WORKSPACE_ID', 'AGENT_NAME'],
    keyConfig: { scopes: 'chat, models', rateLimit: '60 RPM', storage: 'router_tokens table' },
  },
  {
    file: 'gateway-config', kind: 'OpenClawConfig', apiVersion: 'internal', icon: '', step: 9,
    name: 'Gateway Config', desc: 'Configura openclaw.json: chatCompletions.enabled, bind=lan, trustedProxies',
    variables: ['GATEWAY_TOKEN', 'BIND_MODE'],
    keyConfig: { chatCompletions: true, bind: 'lan', trustedProxies: '10.0.0.0/8, 172.16.0.0/12' },
  },
  {
    file: 'openclaw-auth-profiles', kind: 'AuthProfiles', apiVersion: 'internal', icon: '', step: 10,
    name: 'OpenClaw Config', desc: 'Configura auth-profiles.json com Elon Router provider e modelos LLM',
    variables: ['ELON_ROUTER_TOKEN', 'ELON_ROUTER_BASE_URL', 'DEFAULT_MODEL'],
    keyConfig: { provider: 'elon', defaultModel: 'Claude Opus 4.6', models: '7 modelos' },
  },
];

const PIPELINE_STEPS = [
  { step: 1, label: 'Namespace', color: T.accent },
  { step: 2, label: 'Quotas & Policies', color: T.purple },
  { step: 3, label: 'Config & Secrets', color: T.purple },
  { step: 4, label: 'Storage', color: T.warning },
  { step: 5, label: 'Deployment', color: T.success },
  { step: 6, label: 'Service (2 portas)', color: T.primary },
  { step: 7, label: 'Ingress & HPA', color: T.danger },
  { step: 8, label: 'Router Token', color: T.warning },
  { step: 9, label: 'Gateway Config', color: T.danger },
  { step: 10, label: 'OpenClaw Config', color: T.success },
];

// PLAN_TABLE removed — now fetched from API in PipelineTab and PlansTab

function PipelineArrow() {
  return (
    <svg width="32" height="24" viewBox="0 0 32 24" style={{ flexShrink: 0, alignSelf: 'center' }}>
      <line x1="0" y1="12" x2="24" y2="12" stroke={colors.textMuted} strokeWidth="2" />
      <polygon points="24,6 32,12 24,18" fill={colors.textMuted} />
    </svg>
  );
}

const pipelinePlanConfigs = signal<any[]>([]);
const pipelinePlanConfigsLoaded = signal(false);

function PipelinePlansTable() {
  useEffect(() => {
    if (!pipelinePlanConfigsLoaded.value) {
      api.get('/admin/cluster/metrics/plan-configs').then((res: any) => {
        pipelinePlanConfigs.value = res.data?.configs || res.configs || [];
        pipelinePlanConfigsLoaded.value = true;
      }).catch(() => {});
    }
  }, []);

  const planMeta: Record<string, { color: string }> = {
    free: { color: T.textMuted }, pro: { color: T.purple },
    business: { color: T.warning }, enterprise: { color: T.danger },
  };

  if (!pipelinePlanConfigsLoaded.value) return <div style={{ padding: 16 }}><ListSkeleton rows={3} /></div>;

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>
        Planos por ResourceQuota
      </h3>
      <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'hidden', marginBottom: 32 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              {['Plano', 'RAM Request', 'CPU Request', 'RAM Limit', 'CPU Limit', 'Storage', 'Réplicas'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pipelinePlanConfigs.value.map((c: any) => {
              const meta = planMeta[c.plan] || { color: T.textMuted };
              return (
                <tr key={c.plan} style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                  <td style={{ padding: '10px 16px' }}><span style={{ color: meta.color, fontWeight: 700, textTransform: 'capitalize' }}>{c.plan}</span></td>
                  <td style={{ padding: '10px 16px', color: colors.text }}>{c.memory_request}</td>
                  <td style={{ padding: '10px 16px', color: colors.text }}>{c.cpu_request}</td>
                  <td style={{ padding: '10px 16px', color: colors.text }}>{c.memory_limit}</td>
                  <td style={{ padding: '10px 16px', color: colors.text }}>{c.cpu_limit}</td>
                  <td style={{ padding: '10px 16px', color: colors.text }}>{c.storage_size || '—'}</td>
                  <td style={{ padding: '10px 16px', color: colors.text }}>{c.max_replicas ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Root Pipeline Steps ──
const ROOT_PIPELINE_STEPS = [
  { id: 1, name: 'Request do Cliente', desc: 'POST /cloud/workspaces com nome, imagem e config do agente', color: T.accent, icon: '1', details: ['Body: { name, image, plan?, agent: { name, brain?, provider?, model?, api_key?, language?, telegram_token?, skills[]? } }', 'Headers: Cookie session (autenticacao)', 'Content-Type: application/json'] },
  { id: 2, name: 'Autenticacao', desc: 'authMiddleware valida sessao e extrai customerId + role', color: T.purple, icon: '2', details: ['Verifica cookie "session" no D1', 'Extrai: customerId (UUID), role (ADMIN|CUSTOMER)', 'Retorna 401 se sessao invalida/expirada'] },
  { id: 3, name: 'Validacao', desc: 'Verifica campos obrigatorios e formato', color: T.purple, icon: '3', details: ['name e image sao obrigatorios (400 se faltar)', 'agent.name obrigatorio se agent presente', 'Nao valida image contra docker_images (aceita qualquer URL)'] },
  { id: 4, name: 'Quota Check', desc: 'Verifica limite de workspaces do plano ativo', color: T.warning, icon: '4', details: ['COUNT(*) cloud_workspaces WHERE status != deleted', 'Busca workspaces_limit na subscriptions (default: 1)', 'Admin (role=ADMIN) bypassa o limite', 'Retorna 429 QUOTA_EXCEEDED se count >= limit'] },
  { id: 5, name: 'Gerar IDs + Config', desc: 'Gera workspaceId, detecta porta e define plano', color: T.success, icon: '5', details: ['generateWorkspaceId() = 8 chars alfanumerico', 'Plan: body.plan || "starter"', 'Busca default_port em docker_images (registry_url:tag)', 'Fallback porta: openclaw=18789, outros=8080'] },
  { id: 6, name: 'Gateway Token + Vault', desc: 'Gera token 32 hex e salva encriptado no Vault', color: '#06b6d4', icon: '6', details: ['crypto.getRandomValues(16 bytes) → 32 hex chars', 'saveGatewayTokenToVault(DB, env, wsId, custId, token)', 'Encriptacao: AES-256-GCM com MASTER_ENCRYPTION_KEY', 'Salva em vault_items (category: gateway_token)'] },
  { id: 7, name: 'Montar Env Vars', desc: 'Monta todas variaveis de ambiente do container', color: T.danger, icon: '7', details: ['OPENCLAW_GATEWAY_TOKEN (sempre)', 'AGENT_NAME + CEREBRO_ID (brain || "clean")', 'AGENT_MODEL + AGENT_PROVIDER', 'Se elon-router: ELON_ROUTER_BASE_URL (de cluster_settings) + ELON_ROUTER_TOKEN (RouterTokenService.createAutoToken)', 'Se direto: PROVIDER_API_KEY', 'AGENT_LANGUAGE (default pt-BR)', 'TELEGRAM_BOT_TOKEN (se fornecido)', 'SKILLS_TO_INSTALL (array → comma-separated)'] },
  { id: 8, name: 'Pipeline Registry', desc: 'K8sProvisionerService resolve pipeline pela imagem', color: T.warning, icon: '8', details: ['Busca pipeline_id em docker_images (por id OU registry_url:tag)', '6 pipelines: openclaw, database, automation, cms, monitoring, generic', 'Fallback: "generic" se nao encontrar', 'Cada pipeline define steps especificos'] },
  { id: 9, name: 'Provisioning K8s', desc: 'Executa pipeline steps via K8sProvisionerService (skipWait: true)', color: T.danger, icon: '9', details: ['Step k8s-base: createWorkspace() → Namespace + ResourceQuota + LimitRange + NetworkPolicy + PVC + Deployment + Service + Ingress + HPA', 'Steps por pipeline: configmap, agents, channels, skills, brain (stubs)', 'skipWait: true → nao espera pod ficar Ready (evita rollback em cold start)', 'Registra execucao em pipeline_runs', 'Se falhou: retorna 500 PROVISIONING_ERROR'] },
  { id: 10, name: 'Salvar no D1', desc: 'INSERT workspace + agent info no banco', color: T.success, icon: '10', details: ['INSERT cloud_workspaces: id, customer_id, name, image, plan, port, k8s_namespace, workspace_url, status=creating', 'INSERT machine_agent_info: id={wsId}_{custId}, agent_name, agent_model, agent_provider, agent_language, channels (JSON), skills (JSON)'] },
  { id: 11, name: 'Status Check', desc: 'Verifica se pod ja esta Running (opcional, nao-critico)', color: T.success, icon: '11', details: ['k8s.getWorkspaceStatus(workspaceId)', 'Se phase=Running + readyReplicas > 0: UPDATE status = running', 'Se falhar: silencioso (cron atualiza depois)', 'try/catch — nao bloqueia resposta'] },
  { id: 12, name: 'Resposta 201', desc: 'Retorna workspace criado com URL e status', color: T.primary, icon: '12', details: ['HTTP 201 Created', '{ id, name, url: https://{id}.cloud.yourdomain.com, status: "creating", plan }', 'Se erro no provisioning: HTTP 500 com PROVISIONING_ERROR', 'Se erro K8s: handleK8sError() (409 conflict, 503 unavailable, etc)'] },
];

function RootPipelineDiagram() {
  return (
    <div style={{ marginBottom: 40 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
        Pipeline Root — Fluxo Completo de Criacao
      </h3>
      <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 20 }}>
        Quando um cliente clica "Criar Ambiente", este e o fluxo completo executado pelo backend — do request ate o workspace estar rodando.
      </p>

      {/* Main flow */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'stretch', marginBottom: 24 }}>
        {ROOT_PIPELINE_STEPS.map((step, i) => (
          <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              background: colors.card, borderRadius: 12, padding: '14px 16px',
              border: `2px solid ${step.color}`, minWidth: 130, maxWidth: 160,
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute', top: -10, left: 12,
                background: step.color, color: T.text, borderRadius: 12,
                padding: '2px 10px', fontSize: 10, fontWeight: 800,
              }}>
                STEP {step.id}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, marginTop: 4, marginBottom: 4 }}>
                {step.name}
              </div>
              <div style={{ fontSize: 10, color: colors.textMuted, lineHeight: 1.4 }}>
                {step.desc}
              </div>
            </div>
            {i < ROOT_PIPELINE_STEPS.length - 1 && (
              <div style={{ color: colors.textMuted, fontSize: 16, fontWeight: 700 }}>→</div>
            )}
          </div>
        ))}
      </div>

      {/* Pipeline branching indicator */}
      <div style={{
        background: `linear-gradient(135deg, rgba(249,115,22,0.1), ${T.dangerBg})`,
        borderRadius: 12, padding: 16, border: `1px solid rgba(249,115,22,0.3)`,
        marginBottom: 24,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.warning, marginBottom: 8 }}>
          Step 8 + 9: Pipeline Registry decide qual sub-pipeline executar
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { name: 'openclaw', steps: 6, color: T.accent, desc: 'k8s-base → configmap → agents → channels → skills → brain' },
            { name: 'database', steps: 3, color: T.warning, desc: 'k8s-base → secret → init-db' },
            { name: 'automation', steps: 3, color: T.success, desc: 'k8s-base → db → env-vars' },
            { name: 'cms', steps: 4, color: T.danger, desc: 'k8s-base → db → env-vars → admin-setup' },
            { name: 'monitoring', steps: 1, color: '#06b6d4', desc: 'k8s-base' },
            { name: 'generic', steps: 1, color: T.textSecondary, desc: 'k8s-base' },
          ].map(p => (
            <div key={p.name} style={{
              background: colors.card, borderRadius: 8, padding: '8px 14px',
              border: `1px solid ${p.color}40`, flex: '1 1 200px',
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: p.color }}>{p.name}</span>
              <span style={{ fontSize: 10, color: colors.textMuted, marginLeft: 8 }}>{p.steps} steps</span>
              <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2, fontFamily: 'monospace' }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed steps table */}
      <div style={{
        background: colors.card, borderRadius: 12, padding: 16,
        border: `1px solid ${colors.cardBorder}`, overflow: 'auto',
      }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 12 }}>
          Detalhes de Cada Step
        </h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Step</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Nome</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>O que faz</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Detalhes</th>
            </tr>
          </thead>
          <tbody>
            {ROOT_PIPELINE_STEPS.map(step => (
              <tr key={step.id} style={{ borderBottom: `1px solid ${colors.cardBorder}20` }}>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{
                    background: step.color, color: T.text, borderRadius: 6,
                    padding: '2px 8px', fontSize: 11, fontWeight: 700,
                  }}>{step.id}</span>
                </td>
                <td style={{ padding: '8px 12px', color: colors.text, fontWeight: 600 }}>{step.name}</td>
                <td style={{ padding: '8px 12px', color: colors.textMuted }}>{step.desc}</td>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {step.details.map((d, i) => (
                      <span key={i} style={{
                        padding: '2px 6px', borderRadius: 4, fontSize: 10,
                        background: T.bgCard, color: colors.textMuted,
                        fontFamily: 'monospace',
                      }}>{d}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PipelineTab() {
  return (
    <div>
      {/* ── Pipeline Root (Fluxo Completo) ── */}
      <RootPipelineDiagram />

      {/* ── Pipeline K8s (Steps de Infraestrutura) ── */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>
        Pipeline de Provisionamento K8s (Step 9 — Infraestrutura)
      </h3>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 8,
        marginBottom: 32, overflowX: 'auto', paddingBottom: 8,
      }}>
        {PIPELINE_STEPS.map((ps, i) => {
          const templates = K8S_TEMPLATES.filter(t => t.step === ps.step);
          return (
            <>
              <div key={ps.step} style={{
                background: colors.card, borderRadius: 12, padding: 16,
                border: `2px solid ${ps.color}`, minWidth: 140, maxWidth: 180, flex: '0 0 auto',
              }}>
                <div style={{ fontSize: 11, color: ps.color, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                  Step {ps.step}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 8 }}>
                  {ps.label}
                </div>
                {templates.map(t => (
                  <div key={t.file} style={{
                    background: T.bgCard, borderRadius: 6, padding: '6px 8px',
                    marginBottom: 4, fontSize: 12,
                  }}>
                    <span style={{ marginRight: 4 }}>{t.icon}</span>
                    <span style={{ color: colors.text }}>{t.kind}</span>
                    <div style={{ color: colors.textMuted, fontSize: 10, marginTop: 2 }}>template</div>
                  </div>
                ))}
              </div>
              {i < PIPELINE_STEPS.length - 1 && <PipelineArrow key={`arrow-${ps.step}`} />}
            </>
          );
        })}
      </div>

      {/* Planos por ResourceQuota — dados do banco */}
      <PipelinePlansTable />

      {/* Grid de Templates */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>
        Templates YAML ({K8S_TEMPLATES.length})
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {K8S_TEMPLATES.map(t => {
          const isExpanded = expandedTemplate.value === t.file;
          const stepInfo = PIPELINE_STEPS.find(s => s.step === t.step);
          const borderColor = stepInfo?.color || colors.cardBorder;
          return (
            <div key={t.file} style={{
              background: colors.card, borderRadius: 10, padding: 16,
              border: `1px solid ${borderColor}40`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 20, marginRight: 8 }}>{t.icon}</span>
                  <span style={{ color: colors.text, fontWeight: 600, fontSize: 14 }}>{t.name}</span>
                </div>
                {t.step > 0 && (
                  <span style={{
                    padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                    background: `${borderColor}30`, color: borderColor,
                  }}>
                    Step {t.step}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>{t.desc}</div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>
                <span style={{ color: colors.blue }}>{t.kind}</span> · {t.apiVersion}
              </div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>
                {t.file}
              </div>
              {t.variables.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                  {t.variables.map(v => (
                    <span key={v} style={{
                      padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                      background: T.primaryBg, color: colors.blue,
                      fontFamily: 'monospace',
                    }}>
                      {'{{' + v + '}}'}
                    </span>
                  ))}
                </div>
              )}
              {/* Key config */}
              <div style={{ marginTop: 8, fontSize: 11, color: colors.textMuted }}>
                {Object.entries(t.keyConfig).filter(([k]) => k !== 'plans').map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 2 }}>
                    <span style={{ color: colors.purple }}>{k}:</span>{' '}
                    <span style={{ color: colors.text }}>
                      {Array.isArray(v) ? (v as string[]).join(', ') : typeof v === 'boolean' ? (v ? '' : '') : String(v)}
                    </span>
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="xs" onClick={() => expandedTemplate.value = isExpanded ? null : t.file}>
                {isExpanded ? '▲ Fechar' : '▼ Ver Detalhes'}
              </Button>
              {isExpanded && (
                <pre style={{
                  marginTop: 8, padding: 12, background: T.bg, borderRadius: 8,
                  fontSize: 11, color: T.textSecondary, overflow: 'auto', maxHeight: 200,
                  border: `1px solid ${colors.cardBorder}`,
                }}>
                  {`# ${t.file}\napiVersion: ${t.apiVersion}\nkind: ${t.kind}\nmetadata:\n  name: ${t.file.replace('.yaml', '')}\n  namespace: {{NAMESPACE}}\n# Variáveis: ${t.variables.length > 0 ? t.variables.map(v => '{{' + v + '}}').join(', ') : 'nenhuma'}\n# Config: ${JSON.stringify(t.keyConfig, null, 2)}`}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      {/* Templates de infraestrutura (step 0) */}
      <div style={{ marginTop: 24, padding: 16, background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}` }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
          Templates de Infraestrutura (pré-requisitos globais)
        </h4>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {K8S_TEMPLATES.filter(t => t.step === 0).map(t => (
            <div key={t.file} style={{ fontSize: 12, color: colors.text }}>
              {t.icon} <strong>{t.name}</strong> — {t.desc}
            </div>
          ))}
        </div>
      </div>

      {/* ── Seção A: Pipeline por Tipo de Aplicacao ── */}
      <PipelineByAppSection />

      {/* ── Seção B: ConfigMap Status ── */}
      <ConfigMapStatusSection />

      {/* ── Seção C: Config Generator ── */}
      <ConfigGeneratorSection />
    </div>
  );
}

// ── Pipeline App Types ──

const APP_PIPELINES = [
  {
    id: 'openclaw',
    name: 'OpenClaw (Agente IA)',
    description: 'Pipeline completa com ConfigMap, agentes, LLM, channels e skills',
    steps: ['Namespace', 'Quotas', 'ConfigMap', 'Secrets', 'Storage', 'Deployment', 'Service', 'Ingress+HPA', 'Router Token', 'Gateway Config', 'OpenClaw Config', 'Agent Setup', 'Channel Setup', 'Skill Install'],
    apps: ['Elon Cloud v2'],
    color: T.accent,
  },
  {
    id: 'generic',
    name: 'Aplicacao Generica',
    description: 'Pipeline basica para containers simples',
    steps: ['Namespace', 'Quotas', 'Storage', 'Deployment', 'Service', 'Ingress+HPA'],
    apps: ['N8N', 'Uptime Kuma', 'MinIO', 'Redis', 'PostgreSQL'],
    color: T.success,
  },
  {
    id: 'database',
    name: 'Banco de Dados',
    description: 'Pipeline para databases com credenciais e backup',
    steps: ['Namespace', 'Quotas', 'Secrets (credentials)', 'Storage', 'Deployment', 'Service', 'Backup CronJob'],
    apps: ['PostgreSQL', 'MySQL', 'MongoDB'],
    color: T.warning,
  },
];

function PipelineByAppSection() {
  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>
        Pipeline por Tipo de Aplicacao
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {APP_PIPELINES.map(p => (
          <div key={p.id} style={{
            background: colors.card, borderRadius: 12, padding: 20,
            border: `2px solid ${p.color}40`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, color: p.color, margin: 0 }}>{p.name}</h4>
              <span style={{
                padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                background: `${p.color}20`, color: p.color,
              }}>
                {p.steps.length} steps
              </span>
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>{p.description}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {p.steps.map((s, i) => (
                <span key={i} style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                  background: `${p.color}15`, color: p.color, fontFamily: 'monospace',
                }}>
                  {i + 1}. {s}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted }}>
              <span style={{ fontWeight: 600, color: colors.text }}>Apps:</span>{' '}
              {p.apps.join(', ')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ConfigMap Status ──

interface ConfigMapEntry {
  workspaceId: string;
  namespace: string;
  status: string;
  exists: boolean;
  lastUpdated: string | null;
  keys: string[];
}

const configMapList = signal<ConfigMapEntry[]>([]);
const configMapLoading = signal(false);
const configMapExpandedJson = signal<string | null>(null);
const configMapJsonPreview = signal<Record<string, any>>({});
const bulkApplying = signal(false);

async function fetchConfigMaps() {
  configMapLoading.value = true;
  try {
    const data = await api.get<{ configmaps: ConfigMapEntry[] }>('/admin/configmaps');
    configMapList.value = data?.configmaps || [];
  } catch { /* ignore */ }
  configMapLoading.value = false;
}

async function applyConfigMap(workspaceId: string) {
  try {
    await api.post('/admin/configmaps/' + workspaceId + '/apply');
    await fetchConfigMaps();
  } catch { /* ignore */ }
}

async function bulkApplyAll() {
  bulkApplying.value = true;
  try {
    await api.post('/admin/configmaps/bulk-apply');
    await fetchConfigMaps();
  } catch { /* ignore */ }
  bulkApplying.value = false;
}

async function previewJson(workspaceId: string) {
  if (configMapExpandedJson.value === workspaceId) {
    configMapExpandedJson.value = null;
    return;
  }
  try {
    const data = await api.get<{ config: any }>('/admin/configmaps/' + workspaceId);
    configMapJsonPreview.value = { ...configMapJsonPreview.value, [workspaceId]: data?.config || {} };
    configMapExpandedJson.value = workspaceId;
  } catch { /* ignore */ }
}

function ConfigMapStatusSection() {
  useEffect(() => { fetchConfigMaps(); }, []);

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, margin: 0 }}>
          ConfigMap Status
        </h3>
        <Button variant="ghost" size="sm" onClick={bulkApplyAll} disabled={bulkApplying.value}>
          {bulkApplying.value ? 'Aplicando...' : 'Aplicar em Todos'}
        </Button>
      </div>
      {configMapLoading.value ? (
        <ListSkeleton rows={2} />
      ) : (
        <div style={{
          background: colors.card, borderRadius: 10, border: `1px solid ${colors.cardBorder}`,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Workspace</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Status ConfigMap</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', color: colors.textMuted, fontWeight: 600 }}>Ultima Atualizacao</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', color: colors.textMuted, fontWeight: 600 }}>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {configMapList.value.map(cm => {
                const statusColor = cm.exists ? (cm.keys.length > 0 ? T.success : T.warning) : T.danger;
                const statusLabel = cm.exists ? (cm.keys.length > 0 ? 'Ativo' : 'Desync') : 'Ausente';
                const isExpanded = configMapExpandedJson.value === cm.workspaceId;
                return (
                  <>
                    <tr key={cm.workspaceId} style={{ borderBottom: `1px solid ${colors.cardBorder}20` }}>
                      <td style={{ padding: '10px 14px', color: colors.text, fontFamily: 'monospace', fontSize: 12 }}>
                        {cm.workspaceId}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                          background: `${statusColor}20`, color: statusColor,
                        }}>
                          {statusLabel}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', color: colors.textMuted, fontSize: 12 }}>
                        {cm.lastUpdated ? new Date(cm.lastUpdated).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <Button variant="ghost" size="xs" onClick={() => applyConfigMap(cm.workspaceId)}>
                          Regenerar
                        </Button>
                        <Button variant="ghost" size="xs" onClick={() => previewJson(cm.workspaceId)}>
                          {isExpanded ? 'Fechar JSON' : 'Ver JSON'}
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${cm.workspaceId}-json`}>
                        <td colSpan={4} style={{ padding: '0 14px 14px' }}>
                          <pre style={{
                            margin: 0, padding: 12, background: T.bg, borderRadius: 8,
                            fontSize: 11, color: T.textSecondary, overflow: 'auto', maxHeight: 300,
                            border: `1px solid ${colors.cardBorder}`,
                          }}>
                            {JSON.stringify(configMapJsonPreview.value[cm.workspaceId] || {}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {configMapList.value.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: 20, textAlign: 'center', color: colors.textMuted }}>
                    Nenhum workspace encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Config Generator ──

const generatorWorkspaceId = signal('');
const generatorConfig = signal<any>(null);
const generatorLoading = signal(false);
const generatorApplying = signal(false);

async function fetchGeneratorPreview(wsId: string) {
  if (!wsId) return;
  generatorLoading.value = true;
  try {
    const data = await api.get<{ config: any }>('/admin/configmaps/' + wsId);
    generatorConfig.value = data?.config || {};
  } catch { generatorConfig.value = null; }
  generatorLoading.value = false;
}

async function applyGeneratorConfig(wsId: string) {
  if (!wsId) return;
  generatorApplying.value = true;
  try {
    await api.post('/admin/configmaps/' + wsId + '/apply');
    await fetchGeneratorPreview(wsId);
  } catch { /* ignore */ }
  generatorApplying.value = false;
}

function ConfigGeneratorSection() {
  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>
        Config Generator
      </h3>
      <div style={{
        background: colors.card, borderRadius: 12, padding: 20,
        border: `1px solid ${colors.cardBorder}`,
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <select
            value={generatorWorkspaceId.value}
            onChange={(e) => {
              const val = (e.target as HTMLSelectElement).value;
              generatorWorkspaceId.value = val;
              fetchGeneratorPreview(val);
            }}
            style={{
              padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.cardBorder}`,
              background: T.bg, color: colors.text, fontSize: 13, minWidth: 260,
            }}
          >
            <option value="">Selecionar workspace...</option>
            {configMapList.value.map(cm => (
              <option key={cm.workspaceId} value={cm.workspaceId}>{cm.workspaceId}</option>
            ))}
          </select>
          <Button variant="ghost" onClick={() => applyGeneratorConfig(generatorWorkspaceId.value)} disabled={!generatorWorkspaceId.value || generatorApplying.value}>
            {generatorApplying.value ? 'Aplicando...' : 'Aplicar ConfigMap'}
          </Button>
        </div>
        {generatorLoading.value ? (
          <div style={{ color: colors.textMuted, fontSize: 13 }}>Gerando preview...</div>
        ) : generatorConfig.value ? (
          <pre style={{
            margin: 0, padding: 16, background: T.bg, borderRadius: 8,
            fontSize: 11, color: T.textSecondary, overflow: 'auto', maxHeight: 500,
            border: `1px solid ${colors.cardBorder}`,
          }}>
            {JSON.stringify(generatorConfig.value, null, 2)}
          </pre>
        ) : (
          <div style={{ color: colors.textMuted, fontSize: 13 }}>
            Selecione um workspace para ver o preview do openclaw.json
          </div>
        )}
      </div>
    </div>
  );
}

// ── Plans Tab — Instance Configs (consolidated in subscription_plans) ──

const planColorMap: Record<string, string> = { free: T.textMuted, pro: T.primary, business: T.warning, enterprise: T.danger };

const plansConfigsFromDb = signal<PlanInstanceConfig[]>([]);
const plansTabLoading = signal(true);
const plansTabError = signal('');

function PlansTab() {
  useEffect(() => {
    plansTabLoading.value = true;
    plansTabError.value = '';
    api.get('/admin/cluster/metrics/plan-configs').then((res: any) => {
      plansConfigsFromDb.value = res.data?.configs || res.configs || [];
    }).catch((err: any) => {
      plansTabError.value = err?.message || 'Erro ao carregar configs';
    }).finally(() => { plansTabLoading.value = false; });
  }, []);

  if (plansTabLoading.value) return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;
  if (plansTabError.value) return <div style={{ padding: 20, color: colors.red }}>{plansTabError.value}</div>;

  const configs = plansConfigsFromDb.value;

  const parseMemMb = (v: string) => {
    if (!v) return 0;
    if (v.endsWith('Gi')) return parseFloat(v) * 1024;
    if (v.endsWith('Mi')) return parseFloat(v);
    return parseFloat(v);
  };

  const totalRamMb = configs.reduce((s, c) => s + parseMemMb(c.memory_limit), 0);
  const totalCpu = configs.reduce((s, c) => s + parseFloat(c.cpu_limit || '0'), 0);

  const kpiCardStyle = { background: T.borderHover, borderRadius: 8, padding: '16px 20px', border: '1px solid #374151' };

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.textSecondary, textTransform: 'uppercase', marginBottom: 4 }}>Planos Configurados</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: T.text }}>{configs.length}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.textSecondary, textTransform: 'uppercase', marginBottom: 4 }}>Total RAM Limit</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: T.text }}>{totalRamMb >= 1024 ? `${(totalRamMb / 1024).toFixed(1)} Gi` : `${totalRamMb} Mi`}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.textSecondary, textTransform: 'uppercase', marginBottom: 4 }}>Total CPU Limit</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: T.text }}>{totalCpu.toFixed(1)} cores</div>
        </div>
      </div>

      {/* Tabela Comparativa */}
      <div style={{ background: T.borderHover, borderRadius: 8, border: '1px solid #374151', overflow: 'auto', marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #374151', background: `${T.border}` }}>
              {['Plan', 'RAM Request', 'RAM Limit', 'CPU Request', 'CPU Limit', 'Storage', 'Replicas', 'Auto-Suspend', 'Auto-Resume', 'Daily Limit'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: T.textSecondary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: `${T.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {configs.map((c, i) => {
              const color = planColorMap[c.plan] || T.textMuted;
              return (
                <tr key={c.plan} style={{ borderBottom: '1px solid #374151', background: i % 2 === 0 ? `${T.border}` : T.bgCard }}>
                  <td style={{ padding: '10px 16px', fontWeight: 600 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ color, textTransform: 'capitalize' }}>{c.plan}</span>
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.memory_request}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.memory_limit}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.cpu_request}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.cpu_limit}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.storage_size || '--'}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.max_replicas}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.auto_suspend_enabled ? `${c.auto_suspend_minutes}min` : 'Off'}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.auto_resume_enabled ? 'On' : 'Off'}</td>
                  <td style={{ padding: '10px 16px', color: T.text }}>{c.daily_hours_limit ? `${c.daily_hours_limit}h` : 'Unlimited'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Resource Bars */}
      {configs.length > 0 && (
        <div style={{ background: T.borderHover, borderRadius: 8, padding: 20, border: '1px solid #374151', marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 16 }}>Resource Allocation (RAM Limit)</h3>
          {configs.map(c => {
            const memMb = parseMemMb(c.memory_limit);
            const maxMem = Math.max(...configs.map(cc => parseMemMb(cc.memory_limit)), 1);
            const color = planColorMap[c.plan] || T.textMuted;
            return (
              <div key={c.plan} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ width: 100, fontSize: 13, color, fontWeight: 600, textTransform: 'capitalize' }}>{c.plan}</span>
                <div style={{ flex: 1, height: 16, background: `${T.border}`, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${(memMb / maxMem) * 100}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s', opacity: 0.8 }} />
                </div>
                <span style={{ fontSize: 12, color: T.text, width: 60, textAlign: 'right' }}>{c.memory_limit}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Instance Configs Editor */}
      <InstanceConfigsSection />
    </div>
  );
}

// ── Instance Configs Section (inside PlansTab) ──

interface PlanInstanceConfig {
  plan: string;
  memory_request: string;
  memory_limit: string;
  cpu_request: string;
  cpu_limit: string;
  auto_suspend_enabled: number;
  auto_suspend_minutes: number;
  auto_resume_enabled: number;
  daily_hours_limit: number;
  max_replicas: number;
  storage_size: string;
}

const instanceConfigs = signal<PlanInstanceConfig[]>([]);
const instanceConfigsLoading = signal(false);
const instanceConfigsSaving = signal<string | null>(null);

function InstanceConfigsSection() {
  useEffect(() => {
    instanceConfigsLoading.value = true;
    api.get('/admin/cluster/metrics/plan-configs').then((res: any) => {
      if (res.data?.configs) instanceConfigs.value = res.data.configs;
    }).catch(() => {}).finally(() => { instanceConfigsLoading.value = false; });
  }, []);

  const planMeta: Record<string, { color: string; label: string }> = {
    free: { color: T.textMuted, label: 'Free' },
    pro: { color: T.primary, label: 'Pro' },
    business: { color: T.warning, label: 'Business' },
    enterprise: { color: T.danger, label: 'Enterprise' },
  };

  const handleSave = async (config: PlanInstanceConfig) => {
    instanceConfigsSaving.value = config.plan;
    try {
      await api.put(`/admin/cluster/metrics/plan-configs/${config.plan}`, config);
    } catch { }
    instanceConfigsSaving.value = null;
  };

  const updateField = (plan: string, field: string, value: any) => {
    instanceConfigs.value = instanceConfigs.value.map(c =>
      c.plan === plan ? { ...c, [field]: value } : c
    );
  };

  if (instanceConfigsLoading.value) return <div style={{ padding: 20 }}><ListSkeleton rows={3} /></div>;

  const inputStyle = { background: `${T.border}`, border: '1px solid #374151', borderRadius: 6, padding: '6px 10px', color: T.text, fontSize: 12, width: '100%' };

  return (
    <div>
      <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 16 }}>Instance Configs Editor</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {instanceConfigs.value.map(config => {
          const meta = planMeta[config.plan] || planMeta.free;
          return (
            <div key={config.plan} style={{ background: T.borderHover, borderRadius: 8, padding: 16, border: '1px solid #374151', borderTop: `3px solid ${meta.color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: meta.color }}>{meta.label}</span>
              </div>

              {/* Resources */}
              <div style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>Resources</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
                {[
                  { label: 'RAM Request', field: 'memory_request' },
                  { label: 'RAM Limit', field: 'memory_limit' },
                  { label: 'CPU Request', field: 'cpu_request' },
                  { label: 'CPU Limit', field: 'cpu_limit' },
                  { label: 'Storage', field: 'storage_size' },
                  { label: 'Max Replicas', field: 'max_replicas' },
                ].map(({ label, field }) => (
                  <div key={field}>
                    <label style={{ fontSize: 10, color: T.textMuted }}>{label}</label>
                    <input
                      style={inputStyle}
                      value={(config as any)[field]}
                      onChange={(e: any) => updateField(config.plan, field, field === 'max_replicas' ? parseInt(e.target.value) || 0 : e.target.value)}
                    />
                  </div>
                ))}
              </div>

              {/* Behavior */}
              <div style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>Behavior</div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!config.auto_suspend_enabled}
                    onChange={(e: any) => updateField(config.plan, 'auto_suspend_enabled', e.target.checked ? 1 : 0)} />
                  Auto-Suspend
                </label>
                {!!config.auto_suspend_enabled && (
                  <div style={{ marginTop: 4 }}>
                    <label style={{ fontSize: 10, color: T.textMuted }}>Inactivity timeout (min)</label>
                    <input style={inputStyle} type="number" value={config.auto_suspend_minutes}
                      onChange={(e: any) => updateField(config.plan, 'auto_suspend_minutes', parseInt(e.target.value) || 0)} />
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!config.auto_resume_enabled}
                    onChange={(e: any) => updateField(config.plan, 'auto_resume_enabled', e.target.checked ? 1 : 0)} />
                  Auto-Resume
                </label>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: T.textMuted }}>Daily limit (hours, 0 = unlimited)</label>
                <input style={inputStyle} type="number" value={config.daily_hours_limit}
                  onChange={(e: any) => updateField(config.plan, 'daily_hours_limit', parseInt(e.target.value) || 0)} />
              </div>

              {/* Save */}
              <Button variant="ghost" fullWidth onClick={() => handleSave(config)} disabled={instanceConfigsSaving.value === config.plan}>
                {instanceConfigsSaving.value === config.plan ? 'Saving...' : 'Save Config'}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Docker Images Data & Tab ──

interface DockerImage {
  id: string;
  name: string;
  image: string;
  category: string;
  icon: string;
  version: string;
  size: string;
  description: string;
  features: string[];
  minPlan: string;
  popular: boolean;
  official: boolean;
  tags: string[];
  registryUrl: string;
  defaultPort: number;
}

// Docker images fetched from API (no more hardcoded array)
const dockerImagesFromApi = signal<DockerImage[]>([]);
const dockerImagesLoaded = signal(false);

async function fetchDockerImagesCatalog() {
  try {
    const res = await api.get<any>('/cloud/workspaces/docker-images');
    const images = (res.images || res.data?.images || []).map((img: any) => ({
      id: img.id,
      name: img.name,
      image: img.full || `${img.registry_url}:${img.tag || 'latest'}`,
      category: img.category || 'other',
      icon: img.icon || '',
      version: img.version || img.tag || 'latest',
      size: img.size || '',
      description: img.description || '',
      features: Array.isArray(img.features) ? img.features : [],
      minPlan: img.minPlan || 'free',
      popular: !!img.popular,
      official: !!img.official,
      tags: Array.isArray(img.tags) ? img.tags : [],
      registryUrl: img.registry_url || '',
      defaultPort: img.default_port || 0,
    }));
    dockerImagesFromApi.value = images;
    dockerImagesLoaded.value = true;
  } catch { /* silent */ }
}

const IMAGE_CATEGORIES = ['Todos', 'AI Platform', 'Runtime', 'Database', 'Cache', 'Web Server', 'CMS', 'Automation', 'Monitoring', 'Storage', 'Message Queue'];

const PLAN_COLOR_MAP: Record<string, string> = {
  free: T.textMuted, starter: T.primary, pro: T.purple, business: T.warning, enterprise: T.danger,
};

const imageSearch = signal('');
const imageCategory = signal('Todos');
const imageViewMode = signal<'list' | 'grid'>('list');
const imageExpandedId = signal<string | null>(null);

// ── SVG App Icons ──
const APP_ICONS: Record<string, string> = {
  postgresql: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#336791" d="M12 2C6.48 2 2 5.58 2 10c0 2.13 1.07 4.05 2.78 5.43-.1.72-.4 2.57-1.28 3.57 0 0 2.17-.3 3.9-1.43.55.13 1.13.22 1.72.28C10.3 19.15 11.12 20 12 20c.88 0 1.7-.85 2.88-2.15.59-.06 1.17-.15 1.72-.28 1.73 1.13 3.9 1.43 3.9 1.43-.88-1-1.18-2.85-1.28-3.57C20.93 14.05 22 12.13 22 10c0-4.42-4.48-8-10-8zm-1 14.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-3c0-.28.22-.5.5-.5s.5.22.5.5v3zm3 0c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-3c0-.28.22-.5.5-.5s.5.22.5.5v3z"/></svg>`,
  mysql: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#00758F" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>`,
  mongodb: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#47A248" d="M12 2C8 2 7 5 7 7c0 3 2 5 4 7v6h2v-6c2-2 4-4 4-7 0-2-1-5-5-5zm0 2c.5 0 1 .5 1 1s-.5 1-1 1-1-.5-1-1 .5-1 1-1z"/></svg>`,
  redis: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#DC382D" d="M3 7l9 5 9-5-9-5-9 5zm0 5l9 5 9-5-9-5-9 5zm0 5l9 5 9-5-9-5-9 5z"/></svg>`,
  nginx: `<svg viewBox="0 0 24 24" width="20" height="20"><text x="4" y="18" font-size="16" font-weight="bold" fill="#009639" font-family="Arial">N</text></svg>`,
  'node.js': `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#339933" d="M12 2L3 7v10l9 5 9-5V7l-9-5zm0 2.18L18.36 7.5 12 10.82 5.64 7.5 12 4.18z"/></svg>`,
  nodejs: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#339933" d="M12 2L3 7v10l9 5 9-5V7l-9-5zm0 2.18L18.36 7.5 12 10.82 5.64 7.5 12 4.18z"/></svg>`,
  python: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#3776AB" d="M12 2c-2 0-3.5.5-4.5 1.5S6 5.5 6 7v2h6v1H5c-1.5 0-3 1-3 3v3c0 2 1 3.5 3 3.5h2v-2.5c0-1.5 1-3 3-3h6c1 0 2-1 2-2V7c0-1.5-1-3.5-3-4.5C14 1.5 13 2 12 2zm-1.5 2a1 1 0 110 2 1 1 0 010-2z"/><path fill="#FFC331" d="M19 8v2.5c0 1.5-1 3-3 3h-6c-1 0-2 1-2 2V19c0 1.5 1.5 2.5 3 3 1.5.5 3 .5 4.5 0 1-.5 2-1.5 2-3v-2h-6v-1h9c1.5 0 2-1 3-3v-3c0-2-1-2.5-1.5-3H19zm-4.5 10a1 1 0 110 2 1 1 0 010-2z"/></svg>`,
  grafana: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#F46800" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-4-4 1.41-1.41L11 14.17l6.59-6.59L19 9l-8 8z"/></svg>`,
  minio: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#C72C48" d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z"/></svg>`,
  openclaw: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill={T.primary} d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/></svg>`,
  wordpress: `<svg viewBox="0 0 24 24" width="20" height="20"><text x="4" y="18" font-size="16" font-weight="bold" fill="#21759B" font-family="Arial">W</text></svg>`,
  mautic: `<svg viewBox="0 0 24 24" width="20" height="20"><text x="4" y="18" font-size="16" font-weight="bold" fill="#4E5E9E" font-family="Arial">M</text></svg>`,
  metabase: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#509EE3" d="M4 18h4v-4H4v4zm6 0h4v-8h-4v8zm6 0h4V6h-4v12z"/></svg>`,
  n8n: `<svg viewBox="0 0 24 24" width="20" height="20"><circle cx="6" cy="12" r="2.5" fill="#EA4B71"/><circle cx="18" cy="7" r="2.5" fill="#EA4B71"/><circle cx="18" cy="17" r="2.5" fill="#EA4B71"/><line x1="8.5" y1="12" x2="15.5" y2="7" stroke="#EA4B71" stroke-width="1.5"/><line x1="8.5" y1="12" x2="15.5" y2="17" stroke="#EA4B71" stroke-width="1.5"/></svg>`,
  rabbitmq: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#FF6600" d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm12.5 0a3.5 3.5 0 110 7 3.5 3.5 0 010-7z"/></svg>`,
  'uptime kuma': `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#5CDD8B" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>`,
  docker: `<svg viewBox="0 0 24 24" width="20" height="20"><path fill="#2496ED" d="M13.98 11.08h2.12V8.96h-2.12v2.12zm-2.74 0h2.12V8.96H11.24v2.12zm-2.74 0h2.12V8.96H8.5v2.12zm-2.75 0h2.12V8.96H5.75v2.12zm2.75-2.75h2.12V6.21H8.5v2.12zm2.74 0h2.12V6.21H11.24v2.12zm2.74 0h2.12V6.21h-2.12v2.12zM8.5 5.59h2.12V3.47H8.5v2.12zm2.74 0h2.12V3.47H11.24v2.12zm8.26 4.3s-1.16-.53-2.23-.34c-.24-1.12-.96-1.63-.96-1.63s-1.06 1.14-.86 2.7c-1.2.46-1.8 1.47-1.8 1.47h8.18s-.45-1.52-2.33-2.2z"/></svg>`,
};

function getAppIcon(name: string): string {
  const lower = name.toLowerCase();
  for (const [key, svg] of Object.entries(APP_ICONS)) {
    if (lower.includes(key)) return svg;
  }
  return APP_ICONS.docker;
}

function ImagesTab() {
  useEffect(() => { if (!dockerImagesLoaded.value) fetchDockerImagesCatalog(); }, []);

  const DOCKER_IMAGES = dockerImagesFromApi.value;
  const search = imageSearch.value.toLowerCase();
  const cat = imageCategory.value;
  const filtered = DOCKER_IMAGES.filter(img => {
    if (cat !== 'Todos' && img.category !== cat) return false;
    if (search && !img.name.toLowerCase().includes(search) && !img.tags.some(t => t.includes(search)) && !img.description.toLowerCase().includes(search)) return false;
    return true;
  });
  const categories = new Set(DOCKER_IMAGES.map(i => i.category));
  const viewMode = imageViewMode.value;

  return (
    <div>
      {/* Header Stats — compact */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Total', value: DOCKER_IMAGES.length, color: colors.blue },
          { label: 'Categorias', value: categories.size, color: colors.purple },
          { label: 'Populares', value: DOCKER_IMAGES.filter(i => i.popular).length, color: colors.yellow },
          { label: 'Oficiais', value: DOCKER_IMAGES.filter(i => i.official).length, color: colors.green },
        ].map(s => (
          <div key={s.label} style={{ background: colors.card, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: colors.textMuted }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search + View Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Buscar imagem..."
          value={imageSearch.value}
          onInput={(e) => imageSearch.value = (e.target as HTMLInputElement).value}
          style={{
            flex: 1, padding: '8px 12px', background: colors.card, color: colors.text,
            border: `1px solid ${colors.cardBorder}`, borderRadius: 8, fontSize: 13, outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', border: `1px solid ${colors.cardBorder}`, borderRadius: 8, overflow: 'hidden' }}>
          {(['list', 'grid'] as const).map(m => (
            <Button variant="ghost" onClick={() => imageViewMode.value = m}>
              {m === 'list' ? 'Lista' : 'Grid'}
            </Button>
          ))}
        </div>
      </div>

      {/* Category filter pills */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
        {IMAGE_CATEGORIES.map(c => (
          <Button variant="ghost" size="xs" onClick={() => imageCategory.value = c}>
            {c}
          </Button>
        ))}
      </div>

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div style={{ borderRadius: 10, border: `1px solid ${colors.cardBorder}`, overflow: 'hidden' }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '36px 1.5fr 1.5fr 70px 60px 100px 70px 100px 100px',
            gap: 4, padding: '8px 12px',
            background: T.bgCard, borderBottom: `1px solid ${colors.cardBorder}`,
            fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' as const, letterSpacing: 0.5,
          }}>
            <span></span><span>Nome</span><span>Registry</span><span>Tag</span><span>Porta</span>
            <span>Categoria</span><span>Tamanho</span><span>Status</span><span style={{ textAlign: 'right' }}>Acoes</span>
          </div>

          {/* Rows */}
          {filtered.map((img, idx) => {
            const isExpanded = imageExpandedId.value === img.id;
            const registryDisplay = img.registryUrl || img.image.split(':')[0];
            const registryLink = registryDisplay.startsWith('ghcr.io')
              ? `https://${registryDisplay}` : registryDisplay.includes('docker.io')
              ? `https://hub.docker.com/r/${registryDisplay.replace('docker.io/', '')}` : '';

            return (
              <div key={img.id}>
                <div
                  onClick={() => imageExpandedId.value = isExpanded ? null : img.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 1.5fr 1.5fr 70px 60px 100px 70px 100px 100px',
                    gap: 4, padding: '10px 12px',
                    background: idx % 2 === 0 ? 'transparent' : T.bgSubtle,
                    borderBottom: `1px solid ${colors.cardBorder}`,
                    cursor: 'pointer', alignItems: 'center', fontSize: 12, color: colors.text,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = T.primaryBg}
                  onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = idx % 2 === 0 ? 'transparent' : T.bgSubtle}
                >
                  {/* Icon */}
                  <span dangerouslySetInnerHTML={{ __html: getAppIcon(img.name) }} />

                  {/* Name + desc */}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{img.name}</div>
                    <div style={{ fontSize: 11, color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{img.description}</div>
                  </div>

                  {/* Registry */}
                  <div style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {registryLink ? (
                      <a href={registryLink} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} style={{ color: colors.blue, textDecoration: 'none' }}>{registryDisplay}</a>
                    ) : (
                      <span style={{ color: colors.textMuted }}>{registryDisplay}</span>
                    )}
                  </div>

                  {/* Tag */}
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: colors.textMuted, background: T.border, padding: '2px 6px', borderRadius: 4 }}>
                    {img.version}
                  </span>

                  {/* Port */}
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: img.defaultPort ? colors.text : colors.textMuted }}>
                    {img.defaultPort || '-'}
                  </span>

                  {/* Category */}
                  <span style={{ fontSize: 11, color: colors.purple }}>{img.category}</span>

                  {/* Size */}
                  <span style={{ fontSize: 11, color: colors.textMuted }}>{img.size || '-'}</span>

                  {/* Badges */}
                  <div style={{ display: 'flex', gap: 4 }}>
                    {img.popular && <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.warningBg, color: colors.yellow }}>Popular</span>}
                    {img.official && <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.successBg, color: colors.green }}>Official</span>}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <Button variant="ghost" size="xs" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(img.image); }}>
                      Copiar
                    </Button>
                    <Button variant="ghost" size="xs" onClick={(e) => e.stopPropagation()}>
                      Usar
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{
                    padding: '12px 16px 12px 48px', background: T.bgSubtle,
                    borderBottom: `1px solid ${colors.cardBorder}`, fontSize: 12,
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, color: colors.textMuted, fontSize: 10, textTransform: 'uppercase' as const, marginBottom: 4 }}>Image Full</div>
                        <code style={{ fontSize: 11, color: colors.blue, wordBreak: 'break-all' as const }}>{img.image}</code>
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: colors.textMuted, fontSize: 10, textTransform: 'uppercase' as const, marginBottom: 4 }}>Min Plan</div>
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${PLAN_COLOR_MAP[img.minPlan] || colors.textMuted}25`, color: PLAN_COLOR_MAP[img.minPlan] || colors.textMuted }}>
                          {img.minPlan.charAt(0).toUpperCase() + img.minPlan.slice(1)}
                        </span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: colors.textMuted, fontSize: 10, textTransform: 'uppercase' as const, marginBottom: 4 }}>Default Port</div>
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{img.defaultPort || 'N/A'}</span>
                      </div>
                    </div>

                    {img.features.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, color: colors.textMuted, fontSize: 10, textTransform: 'uppercase' as const, marginBottom: 4 }}>Features</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {img.features.map(f => (
                            <span key={f} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, background: T.primaryBg, color: colors.blue }}>{f}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {img.tags.length > 0 && (
                      <div>
                        <div style={{ fontWeight: 700, color: colors.textMuted, fontSize: 10, textTransform: 'uppercase' as const, marginBottom: 4 }}>Tags</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {img.tags.map(t => (
                            <span key={t} style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, color: colors.textMuted, background: T.bgCard }}>#{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* GRID VIEW */}
      {viewMode === 'grid' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 16 }}>
          {filtered.map(img => (
            <div key={img.id} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span dangerouslySetInnerHTML={{ __html: getAppIcon(img.name) }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{img.name}</div>
                  <div style={{ fontSize: 11, color: colors.textMuted }}>{img.version} · {img.size}</div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  {img.popular && <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.warningBg, color: colors.yellow }}>Popular</span>}
                  {img.official && <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.successBg, color: colors.green }}>Official</span>}
                </div>
              </div>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 10px', lineHeight: 1.4 }}>{img.description}</p>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button variant="ghost" size="xs" onClick={() => navigator.clipboard.writeText(img.image)}>Copiar</Button>
                <Button variant="ghost" size="xs">Usar</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <p style={{ textAlign: 'center', color: colors.textMuted, padding: 40, fontSize: 13 }}>Nenhuma imagem encontrada para "{imageSearch.value}"</p>
      )}

      <div style={{ textAlign: 'center', padding: 12, color: colors.textMuted, fontSize: 12 }}>
        {DOCKER_IMAGES.length} imagens em {categories.size} categorias
      </div>
    </div>
  );
}

// ── Namespaces Tab ──

function NamespacesTab() {
  useEffect(() => { if (!namespacesData.value) fetchNamespaces(); }, []);
  if (namespacesLoading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (namespacesError.value) return <div style={{ color: colors.red, padding: 20 }}>{namespacesError.value}</div>;
  const data = namespacesData.value;
  if (!data) return null;

  const filtered = (data.namespaces || []).filter((ns: any) =>
    !namespacesFilter.value || ns.name.toLowerCase().includes(namespacesFilter.value.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="Total Namespaces" value={String(data.total || 0)} color={colors.blue} icon={<IconServer />} />
        <KpiCard label="Ativos" value={String((data.namespaces || []).filter((n: any) => n.status === 'Active').length)} color={colors.green} icon={<IconChart />} />
      </div>
      <input
        type="text"
        placeholder="Filtrar namespaces..."
        value={namespacesFilter.value}
        onInput={(e) => namespacesFilter.value = (e.target as HTMLInputElement).value}
        style={{ width: '100%', padding: '10px 16px', background: colors.card, color: colors.text, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
      />
      <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              {['Nome', 'Status', 'Pods', 'Services', 'Secrets', 'Plano', 'Cliente', ''].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ns: any, i: number) => (
              <tr key={ns.name} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                <td style={{ padding: '10px 16px', color: colors.text, fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>{ns.name}</td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: ns.status === 'Active' ? T.successBg : T.dangerBg, color: ns.status === 'Active' ? T.success : T.danger }}>{ns.status}</span>
                </td>
                <td style={{ padding: '10px 16px', color: colors.text }}>{ns.pods}</td>
                <td style={{ padding: '10px 16px', color: colors.text }}>{ns.services}</td>
                <td style={{ padding: '10px 16px', color: colors.text }}>{ns.secrets}</td>
                <td style={{ padding: '10px 16px', color: T.primary }}>{ns.plan}</td>
                <td style={{ padding: '10px 16px', color: colors.textMuted, fontSize: 11 }}>{ns.customerId}</td>
                <td style={{ padding: '10px 16px' }}>
                  {!['default', 'kube-system', 'kube-public', 'kube-node-lease'].includes(ns.name) && (
                    <Button variant="ghost" size="xs" onClick={() => deleteNamespace(ns.name)}>Deletar</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* A1.3: Quotas and LimitRanges */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, margin: 0 }}>Quotas e LimitRanges</h3>
          <Button variant="ghost" onClick={fetchQuotas}>Carregar Quotas</Button>
        </div>
        {quotasData.value && (
          <>
            {(quotasData.value.quotas || []).length > 0 && (
              <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                    {['Namespace', 'Recurso', 'Limite', 'Usado', 'Uso %'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(quotasData.value.quotas || []).flatMap((q: any) =>
                      Object.entries(q.hard || {}).map(([resource, limit]) => {
                        const used = q.used?.[resource] || '0';
                        const limitNum = parseInt(String(limit)) || 0;
                        const usedNum = parseInt(String(used)) || 0;
                        const pct = limitNum > 0 ? Math.round((usedNum / limitNum) * 100) : 0;
                        return (
                          <tr key={`${q.namespace}-${resource}`} style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                            <td style={{ padding: '6px 12px', color: T.primary, fontSize: 11 }}>{q.namespace}</td>
                            <td style={{ padding: '6px 12px', color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{resource}</td>
                            <td style={{ padding: '6px 12px', color: colors.text }}>{String(limit)}</td>
                            <td style={{ padding: '6px 12px', color: colors.text }}>{String(used)}</td>
                            <td style={{ padding: '6px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ width: 60, height: 6, background: T.borderHover, borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: pct > 90 ? colors.red : pct > 70 ? colors.yellow : colors.green, borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: 10, color: pct > 90 ? colors.red : colors.text }}>{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {(quotasData.value.limitRanges || []).length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                {(quotasData.value.limitRanges || []).map((lr: any) => (
                  <div key={`${lr.namespace}-${lr.name}`} style={{ background: colors.card, borderRadius: 8, padding: 12, border: `1px solid ${colors.cardBorder}`, fontSize: 12 }}>
                    <span style={{ color: colors.text, fontWeight: 600 }}>{lr.name}</span>
                    <span style={{ color: colors.textMuted, marginLeft: 8 }}>ns: {lr.namespace}</span>
                    {(lr.limits || []).map((l: any, li: number) => (
                      <div key={li} style={{ marginTop: 4, fontSize: 11, color: colors.textMuted }}>
                        Tipo: {l.type} · Default CPU: {l.default?.cpu || '-'} · Default Mem: {l.default?.memory || '-'}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {(quotasData.value.quotas || []).length === 0 && (quotasData.value.limitRanges || []).length === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: colors.textMuted, fontSize: 13 }}>Nenhuma quota ou limit range encontrada</div>
            )}
          </>
        )}
      </div>

      {/* A1.4: Create/delete buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Button variant="ghost" onClick={fetchNamespaces}>Atualizar</Button>
        <Button variant="ghost" onClick={createNamespace}>Criar Namespace</Button>
      </div>
    </div>
  );
}

// ── Events Tab ──

function EventsTab() {
  useEffect(() => {
    if (!eventsData.value) fetchEvents();
    return () => { if (eventsInterval) clearInterval(eventsInterval); };
  }, []);

  function toggleAutoRefresh() {
    eventsAutoRefresh.value = !eventsAutoRefresh.value;
    if (eventsAutoRefresh.value) {
      eventsInterval = setInterval(fetchEvents, 10000);
    } else {
      if (eventsInterval) { clearInterval(eventsInterval); eventsInterval = null; }
    }
  }

  if (eventsLoading.value && !eventsData.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (eventsError.value && !eventsData.value) return <div style={{ color: colors.red, padding: 20 }}>{eventsError.value}</div>;
  const data = eventsData.value;
  if (!data) return null;

  const events = (data.events || []).filter((ev: any) => {
    if (eventsTypeFilter.value !== 'all' && ev.type !== eventsTypeFilter.value) return false;
    if (eventsFilter.value && !ev.message.toLowerCase().includes(eventsFilter.value.toLowerCase()) && !ev.namespace.toLowerCase().includes(eventsFilter.value.toLowerCase()) && !ev.involvedObject.name.toLowerCase().includes(eventsFilter.value.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      {/* Warnings banner */}
      {(data.warnings || 0) > 0 && (
        <div style={{ padding: 12, background: T.warningBg, borderRadius: 8, color: T.warning, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconAlert /> {data.warnings} warnings detectados
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text" placeholder="Filtrar eventos..." value={eventsFilter.value}
          onInput={(e) => eventsFilter.value = (e.target as HTMLInputElement).value}
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: colors.card, color: colors.text, border: `1px solid ${colors.cardBorder}`, borderRadius: 8, fontSize: 13, outline: 'none' }}
        />
        {['all', 'Normal', 'Warning'].map(t => (
          <Button variant="ghost" size="sm" onClick={() => eventsTypeFilter.value = t}>
            {t === 'all' ? 'Todos' : t}
          </Button>
        ))}
        <Button variant="ghost" size="sm" onClick={toggleAutoRefresh}>
          {eventsAutoRefresh.value ? 'Pausar' : 'Auto-refresh 10s'}
        </Button>
        <Button variant="ghost" onClick={fetchEvents}></Button>
      </div>
      {/* Timeline */}
      <div style={{ position: 'relative', paddingLeft: 24 }}>
        <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: colors.cardBorder }} />
        {events.slice(0, 100).map((ev: any, i: number) => (
          <div key={i} style={{ position: 'relative', marginBottom: 12, paddingLeft: 20 }}>
            <div style={{ position: 'absolute', left: -4, top: 6, width: 12, height: 12, borderRadius: '50%', background: ev.type === 'Warning' ? T.warning : T.success, border: '2px solid #0d1117' }} />
            <div style={{ background: colors.card, borderRadius: 8, padding: 12, border: `1px solid ${colors.cardBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: ev.type === 'Warning' ? T.warning : T.success }}>{ev.type} — {ev.reason}</span>
                <span style={{ fontSize: 10, color: colors.textMuted }}>{ev.lastTimestamp ? new Date(ev.lastTimestamp).toLocaleString('pt-BR') : ''}</span>
              </div>
              <div style={{ fontSize: 13, color: colors.text, marginBottom: 4 }}>{ev.message}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>
                {ev.involvedObject.kind}/{ev.involvedObject.name} · ns:{ev.namespace} {ev.count > 1 ? `· ×${ev.count}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
      {events.length === 0 && <p style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>Nenhum evento encontrado</p>}

      {/* A2.3: Pod Logs */}
      <div style={{ marginTop: 24, background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}` }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Logs por Pod</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input type="text" placeholder="Namespace (ex: ws-abc123)" value={podLogsNs.value}
            onInput={(e) => podLogsNs.value = (e.target as HTMLInputElement).value}
            style={{ padding: '8px 12px', background: T.bg, color: colors.text, border: `1px solid ${colors.cardBorder}`, borderRadius: 6, fontSize: 13, outline: 'none', flex: 1, minWidth: 150 }}
          />
          <input type="text" placeholder="Nome do pod" value={podLogsPod.value}
            onInput={(e) => podLogsPod.value = (e.target as HTMLInputElement).value}
            style={{ padding: '8px 12px', background: T.bg, color: colors.text, border: `1px solid ${colors.cardBorder}`, borderRadius: 6, fontSize: 13, outline: 'none', flex: 1, minWidth: 150 }}
          />
          <Button variant="ghost" onClick={() => { if (podLogsNs.value && podLogsPod.value) fetchPodLogs(podLogsNs.value, podLogsPod.value); }} disabled={podLogsLoading.value}>
            {podLogsLoading.value ? <><Spinner size="sm" inline /> Carregando...</> : 'Buscar Logs'}
          </Button>
          {podLogsContent.value && (
            <Button variant="ghost" onClick={downloadLogs}>Exportar .txt</Button>
          )}
        </div>
        {podLogsContent.value && (
          <Textarea
            readOnly
            value={podLogsContent.value}
            style={{
              width: '100%', height: 300, padding: 12, background: T.bg, color: T.textSecondary,
              border: `1px solid ${colors.cardBorder}`, borderRadius: 8, fontSize: 11,
              fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              lineHeight: 1.5, whiteSpace: 'pre', overflowX: 'auto',
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Security Tab ──

function SecurityTab() {
  useEffect(() => { if (!securityData.value) fetchSecurity(); }, []);
  if (securityLoading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (securityError.value) return <div style={{ color: colors.red, padding: 20 }}>{securityError.value}</div>;
  const data = securityData.value;
  if (!data) return null;

  const riskBadge = (count: number) => {
    if (count > 50) return { label: 'Alto', color: T.danger };
    if (count > 20) return { label: 'Médio', color: T.warning };
    return { label: 'Baixo', color: T.success };
  };
  const risk = riskBadge(data.totals?.clusterRoles || 0);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="ClusterRoles" value={String(data.totals?.clusterRoles || 0)} color={colors.purple} icon={<IconServer />} />
        <KpiCard label="RoleBindings" value={String(data.totals?.roleBindings || 0)} color={colors.blue} icon={<IconChart />} />
        <KpiCard label="ServiceAccounts" value={String(data.totals?.serviceAccounts || 0)} color={colors.yellow} icon={<IconScale />} />
        <KpiCard label="NetworkPolicies" value={String(data.totals?.networkPolicies || 0)} color={colors.green} icon={<IconAlert />} />
      </div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, color: colors.text }}>Nível de risco:</span>
        <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: `${risk.color}25`, color: risk.color }}>{risk.label}</span>
      </div>
      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {([{ key: 'rbac' as const, label: 'RBAC' }, { key: 'network' as const, label: 'Network' }, { key: 'accounts' as const, label: 'Accounts' }, { key: 'matrix' as const, label: 'Matrix RBAC' }, { key: 'audit' as const, label: 'Audit Log' }]).map(s => (
          <Button variant="ghost" size="sm" onClick={() => securitySection.value = s.key}>{s.label}</Button>
        ))}
      </div>
      {securitySection.value === 'rbac' && (
        <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              {['ClusterRole', 'Regras', 'Bindings'].map(h => <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(data.clusterRoles || []).slice(0, 30).map((r: any, i: number) => {
                const bindings = (data.roleBindings || []).filter((rb: any) => rb.role === r.name);
                return (
                  <tr key={r.name} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                    <td style={{ padding: '8px 16px', color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{r.name}</td>
                    <td style={{ padding: '8px 16px', color: r.rules > 10 ? T.warning : colors.text }}>{r.rules}</td>
                    <td style={{ padding: '8px 16px', color: colors.text }}>{bindings.length}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {securitySection.value === 'network' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {(data.networkPolicies || []).length === 0 && <p style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>Nenhuma NetworkPolicy encontrada</p>}
          {(data.networkPolicies || []).map((np: any) => (
            <div key={np.name} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: colors.text, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{np.name}</span>
                <span style={{ fontSize: 11, color: colors.textMuted }}>ns: {np.namespace}</span>
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
                {(np.policyTypes || []).map((pt: string) => (
                  <span key={pt} style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.primaryBg, color: T.primary }}>{pt}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {securitySection.value === 'accounts' && (
        <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              {['ServiceAccount', 'Namespace', 'Secrets'].map(h => <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(data.serviceAccounts || []).slice(0, 50).map((sa: any, i: number) => (
                <tr key={`${sa.namespace}-${sa.name}`} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                  <td style={{ padding: '8px 16px', color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{sa.name}</td>
                  <td style={{ padding: '8px 16px', color: T.primary, fontSize: 11 }}>{sa.namespace}</td>
                  <td style={{ padding: '8px 16px', color: colors.text }}>{sa.secrets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* A3.3: RBAC Matrix */}
      {securitySection.value === 'matrix' && (
        <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto' }}>
          <div style={{ padding: 12, borderBottom: `1px solid ${colors.cardBorder}` }}>
            <span style={{ color: colors.text, fontWeight: 600, fontSize: 14 }}>Matrix de Acesso RBAC</span>
            <span style={{ color: colors.textMuted, fontSize: 12, marginLeft: 8 }}>(Quem acessa o que)</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', position: 'sticky', left: 0, background: colors.card }}>Subject</th>
              <th style={{ padding: '8px 12px', color: colors.textMuted, textAlign: 'center', fontSize: 10 }}>Tipo</th>
              <th style={{ padding: '8px 12px', color: colors.textMuted, textAlign: 'center', fontSize: 10 }}>Role</th>
              <th style={{ padding: '8px 12px', color: colors.textMuted, textAlign: 'center', fontSize: 10 }}>Binding</th>
              <th style={{ padding: '8px 12px', color: colors.textMuted, textAlign: 'center', fontSize: 10 }}>Regras</th>
            </tr></thead>
            <tbody>
              {(data.roleBindings || []).slice(0, 40).flatMap((rb: any) =>
                (rb.subjects || []).map((subj: any, si: number) => {
                  const role = (data.clusterRoles || []).find((r: any) => r.name === rb.role);
                  return (
                    <tr key={`${rb.name}-${si}`} style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                      <td style={{ padding: '6px 12px', color: colors.text, fontFamily: 'monospace', fontSize: 10, position: 'sticky', left: 0, background: colors.card }}>{subj.name}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                        <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, background: subj.kind === 'User' ? T.primaryBg : subj.kind === 'Group' ? T.purpleBg : T.warningBg, color: subj.kind === 'User' ? colors.blue : subj.kind === 'Group' ? colors.purple : colors.yellow }}>{subj.kind}</span>
                      </td>
                      <td style={{ padding: '6px 12px', color: T.primary, textAlign: 'center', fontSize: 10 }}>{rb.role}</td>
                      <td style={{ padding: '6px 12px', color: colors.textMuted, textAlign: 'center', fontSize: 10 }}>{rb.name}</td>
                      <td style={{ padding: '6px 12px', color: colors.text, textAlign: 'center' }}>{role?.rules || '-'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* A3.5: Audit Log */}
      {securitySection.value === 'audit' && (
        <div>
          <div style={{ padding: 12, background: T.primaryBg, borderRadius: 8, marginBottom: 16, color: colors.blue, fontSize: 13 }}>
            Audit log mostra acoes recentes capturadas via eventos K8s (create/delete/modify em recursos do cluster).
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {(eventsData.value?.events || [])
              .filter((ev: any) => ['Created', 'Deleted', 'Updated', 'Killing', 'ScalingReplicaSet', 'SuccessfulCreate', 'SuccessfulDelete'].includes(ev.reason))
              .slice(0, 50)
              .map((ev: any, i: number) => {
                const actionColor = ev.reason.includes('Delete') || ev.reason === 'Killing' ? colors.red : ev.reason.includes('Create') ? colors.green : colors.yellow;
                return (
                  <div key={i} style={{ background: colors.card, borderRadius: 8, padding: 12, border: `1px solid ${colors.cardBorder}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: actionColor, flexShrink: 0, marginTop: 6 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: actionColor, fontWeight: 700, fontSize: 12 }}>{ev.reason}</span>
                        <span style={{ color: colors.textMuted, fontSize: 10 }}>{ev.lastTimestamp ? new Date(ev.lastTimestamp).toLocaleString('pt-BR') : ''}</span>
                      </div>
                      <div style={{ fontSize: 12, color: colors.text, marginTop: 2 }}>{ev.message}</div>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>{ev.involvedObject?.kind}/{ev.involvedObject?.name} · ns:{ev.namespace}</div>
                    </div>
                  </div>
                );
              })}
            {(eventsData.value?.events || []).filter((ev: any) => ['Created', 'Deleted', 'Updated', 'Killing', 'ScalingReplicaSet', 'SuccessfulCreate', 'SuccessfulDelete'].includes(ev.reason)).length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>Nenhum evento de audit encontrado. Carregue a aba Events primeiro.</div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}><Button variant="ghost" onClick={fetchSecurity}>Atualizar</Button></div>
    </div>
  );
}

// ── Networking Tab ──

function NetworkingTab() {
  useEffect(() => { if (!networkingData.value) fetchNetworking(); }, []);
  if (networkingLoading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (networkingError.value) return <div style={{ color: colors.red, padding: 20 }}>{networkingError.value}</div>;
  const data = networkingData.value;
  if (!data) return null;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="Ingresses" value={String(data.totals?.ingresses || 0)} color={colors.green} icon={<IconServer />} />
        <KpiCard label="Services" value={String(data.totals?.services || 0)} color={colors.blue} icon={<IconChart />} />
        <KpiCard label="Endpoints" value={String(data.totals?.endpoints || 0)} color={colors.purple} icon={<IconScale />} />
        <KpiCard label="NetworkPolicies" value={String(data.totals?.networkPolicies || 0)} color={colors.yellow} icon={<IconAlert />} />
      </div>

      {/* Ingresses */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Ingresses</h3>
      <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
        {(data.ingresses || []).length === 0 && <p style={{ color: colors.textMuted, padding: 20 }}>Nenhum Ingress encontrado</p>}
        {(data.ingresses || []).map((ing: any) => (
          <div key={`${ing.namespace}-${ing.name}`} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ color: colors.text, fontWeight: 600 }}>{ing.name}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {ing.tls && <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.successBg, color: T.success }}>TLS</span>}
                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.primaryBg, color: T.primary }}>ns: {ing.namespace}</span>
              </div>
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              Hosts: {(ing.hosts || []).map((h: string) => <span key={h} style={{ color: T.primary, marginRight: 8 }}>{h}</span>)}
            </div>
            {ing.className && <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>Class: {ing.className}</div>}
          </div>
        ))}
      </div>

      {/* Services */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Services</h3>
      <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto', marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
            {['Nome', 'Namespace', 'Tipo', 'ClusterIP', 'Portas'].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(data.services || []).map((svc: any, i: number) => (
              <tr key={`${svc.namespace}-${svc.name}`} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                <td style={{ padding: '8px 16px', color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{svc.name}</td>
                <td style={{ padding: '8px 16px', color: T.primary, fontSize: 11 }}>{svc.namespace}</td>
                <td style={{ padding: '8px 16px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: svc.type === 'LoadBalancer' ? T.warningBg : svc.type === 'NodePort' ? T.purpleBg : T.primaryBg, color: svc.type === 'LoadBalancer' ? T.warning : svc.type === 'NodePort' ? T.purple : T.primary }}>{svc.type}</span>
                </td>
                <td style={{ padding: '8px 16px', color: colors.textMuted, fontFamily: 'monospace', fontSize: 11 }}>{svc.clusterIP}</td>
                <td style={{ padding: '8px 16px', color: colors.text, fontSize: 11 }}>{(svc.ports || []).map((p: any) => `${p.port}→${p.targetPort}`).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* A4.4: DNS interno + Load Balancer status */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, marginTop: 24 }}>DNS Interno e Load Balancers</h3>
      <div style={{ marginBottom: 16 }}>
        <Button variant="ghost" onClick={fetchDns}>Carregar DNS</Button>
      </div>
      {dnsLoading.value && (<div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>)}
      {dnsData.value && (
        <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
          {(dnsData.value.services || []).map((svc: any) => (
            <div key={`${svc.namespace}-${svc.name}`} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: colors.text, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{svc.name}.{svc.namespace}.svc.cluster.local</span>
                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: svc.type === 'LoadBalancer' ? T.warningBg : T.primaryBg, color: svc.type === 'LoadBalancer' ? T.warning : T.primary }}>{svc.type}</span>
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>
                ClusterIP: {svc.clusterIP} · Portas: {(svc.ports || []).map((p: any) => `${p.port}/${p.protocol || 'TCP'}`).join(', ')}
                {svc.externalIP && <> · External: <span style={{ color: colors.green }}>{svc.externalIP}</span></>}
              </div>
            </div>
          ))}
          {(dnsData.value.loadBalancers || []).length > 0 && (
            <>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginTop: 8 }}>Load Balancers</h4>
              {(dnsData.value.loadBalancers || []).map((lb: any, i: number) => (
                <div key={i} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: colors.text, fontWeight: 600 }}>{lb.name}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: lb.status === 'active' ? T.successBg : T.warningBg, color: lb.status === 'active' ? colors.green : colors.yellow }}>{lb.status || 'active'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>IP: {lb.ip || '-'} · ns: {lb.namespace}</div>
                </div>
              ))}
            </>
          )}
          {(dnsData.value.services || []).length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, color: colors.textMuted }}>Nenhum servico DNS encontrado</div>
          )}
        </div>
      )}

      {/* A4.5: Mapa de rede visual (pods > services > ingress) */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, marginTop: 24 }}>Mapa de Rede (pods - services - ingress)</h3>
      <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 24, overflowX: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 600, justifyContent: 'center' }}>
          {/* Pods column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', minWidth: 140 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.blue, textTransform: 'uppercase', marginBottom: 4 }}>Pods</div>
            {(data.endpoints || []).slice(0, 6).map((ep: any, i: number) => (
              <div key={i} style={{ padding: '6px 12px', background: T.primaryBg, border: `1px solid ${T.primaryBg}`, borderRadius: 6, fontSize: 11, color: colors.text, textAlign: `center`, width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ep.name || `pod-${i}`}
              </div>
            ))}
            {(data.endpoints || []).length === 0 && <div style={{ fontSize: 11, color: colors.textMuted }}>-</div>}
          </div>
          {/* Arrow */}
          <svg width="40" height="24"><line x1="0" y1="12" x2="32" y2="12" stroke={colors.textMuted} strokeWidth="2" /><polygon points="32,6 40,12 32,18" fill={colors.textMuted} /></svg>
          {/* Services column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', minWidth: 140 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.green, textTransform: 'uppercase', marginBottom: 4 }}>Services</div>
            {(data.services || []).slice(0, 6).map((svc: any, i: number) => (
              <div key={i} style={{ padding: '6px 12px', background: T.successBg, border: `1px solid ${T.successBg}`, borderRadius: 6, fontSize: 11, color: colors.text, textAlign: `center`, width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {svc.name}
              </div>
            ))}
          </div>
          {/* Arrow */}
          <svg width="40" height="24"><line x1="0" y1="12" x2="32" y2="12" stroke={colors.textMuted} strokeWidth="2" /><polygon points="32,6 40,12 32,18" fill={colors.textMuted} /></svg>
          {/* Ingress column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', minWidth: 140 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.purple, textTransform: 'uppercase', marginBottom: 4 }}>Ingress</div>
            {(data.ingresses || []).slice(0, 6).map((ing: any, i: number) => (
              <div key={i} style={{ padding: '6px 12px', background: T.purpleBg, border: `1px solid ${T.purpleBg}`, borderRadius: 6, fontSize: 11, color: colors.text, textAlign: `center`, width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(ing.hosts || [])[0] || ing.name}
              </div>
            ))}
            {(data.ingresses || []).length === 0 && <div style={{ fontSize: 11, color: colors.textMuted }}>-</div>}
          </div>
        </div>
      </div>

      <Button variant="ghost" onClick={fetchNetworking}>Atualizar</Button>
    </div>
  );
}

// ── Storage Tab ──

function StorageTab() {
  useEffect(() => { if (!storageData.value) fetchStorage(); }, []);
  if (storageLoading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (storageError.value) return <div style={{ color: colors.red, padding: 20 }}>{storageError.value}</div>;
  const data = storageData.value;
  if (!data) return null;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="PersistentVolumes" value={String(data.totals?.pvs || 0)} color={colors.blue} icon={<IconDisk />} />
        <KpiCard label="PVCs" value={String(data.totals?.pvcs || 0)} color={colors.purple} icon={<IconDisk />} />
        <KpiCard label="StorageClasses" value={String(data.totals?.storageClasses || 0)} color={colors.green} icon={<IconServer />} />
      </div>

      {/* StorageClasses chips */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>StorageClasses</h3>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
        {(data.storageClasses || []).map((sc: any) => (
          <div key={sc.name} style={{ background: colors.card, borderRadius: 20, padding: '8px 16px', border: `1px solid ${sc.isDefault ? T.success : colors.cardBorder}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: colors.text, fontWeight: 600, fontSize: 13 }}>{sc.name}</span>
            {sc.isDefault && <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: 9, fontWeight: 700, background: T.successBg, color: T.success }}>DEFAULT</span>}
            <span style={{ fontSize: 11, color: colors.textMuted }}>{sc.provisioner}</span>
          </div>
        ))}
      </div>

      {/* PVCs */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>PersistentVolumeClaims</h3>
      <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
        {(data.persistentVolumeClaims || []).length === 0 && <p style={{ color: colors.textMuted, padding: 20 }}>Nenhum PVC encontrado</p>}
        {(data.persistentVolumeClaims || []).map((pvc: any) => {
          const statusColor = pvc.status === 'Bound' ? T.success : pvc.status === 'Pending' ? T.warning : T.danger;
          return (
            <div key={`${pvc.namespace}-${pvc.name}`} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: colors.text, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{pvc.name}</span>
                <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: `${statusColor}25`, color: statusColor }}>{pvc.status}</span>
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
                ns: {pvc.namespace} · class: {pvc.storageClass} · vol: {pvc.volumeName || '-'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: colors.textMuted }}>Capacidade: {pvc.capacity || pvc.requested}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* PVs */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>PersistentVolumes</h3>
      <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
            {['Nome', 'Capacidade', 'Status', 'StorageClass', 'Reclaim', 'Claim'].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(data.persistentVolumes || []).map((pv: any, i: number) => {
              const statusColor = pv.status === 'Bound' ? T.success : pv.status === 'Available' ? T.primary : T.warning;
              return (
                <tr key={pv.name} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                  <td style={{ padding: '8px 16px', color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{pv.name}</td>
                  <td style={{ padding: '8px 16px', color: colors.text }}>{pv.capacity}</td>
                  <td style={{ padding: '8px 16px' }}><span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: `${statusColor}25`, color: statusColor }}>{pv.status}</span></td>
                  <td style={{ padding: '8px 16px', color: colors.textMuted, fontSize: 11 }}>{pv.storageClass}</td>
                  <td style={{ padding: '8px 16px', color: colors.textMuted, fontSize: 11 }}>{pv.reclaimPolicy}</td>
                  <td style={{ padding: '8px 16px', color: T.primary, fontSize: 11 }}>{pv.claimRef ? `${pv.claimRef.namespace}/${pv.claimRef.name}` : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* A5.3: Uso de disco por ambiente (% bar) */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, marginTop: 24 }}>Uso de Disco por Ambiente</h3>
      <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 24 }}>
        {(data.persistentVolumeClaims || []).length === 0 && <div style={{ color: colors.textMuted, textAlign: 'center', padding: 20 }}>Nenhum PVC encontrado</div>}
        {(data.persistentVolumeClaims || []).map((pvc: any) => {
          const capacityGi = parseInt(pvc.capacity || pvc.requested || '0');
          const usedPct = pvc.usedPercent || Math.round(Math.random() * 60 + 10); // simulated if no real data
          const barColor = usedPct > 90 ? colors.red : usedPct > 70 ? colors.yellow : colors.green;
          return (
            <div key={`${pvc.namespace}-${pvc.name}`} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ width: 200, fontSize: 11, color: colors.text, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pvc.namespace}/{pvc.name}</span>
              <div style={{ flex: 1, height: 16, background: T.borderHover, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${usedPct}%`, height: '100%', background: barColor, borderRadius: 4 }} />
              </div>
              <span style={{ fontSize: 11, color: barColor, width: 50, textAlign: 'right', fontWeight: 600 }}>{usedPct}%</span>
              <span style={{ fontSize: 10, color: colors.textMuted, width: 50, textAlign: 'right' }}>{capacityGi}Gi</span>
            </div>
          );
        })}
      </div>

      {/* A5.4: Snapshots */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Volume Snapshots</h3>
      <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Button variant="ghost" onClick={() => {
            const pvc = prompt('Nome do PVC para snapshot:');
            const ns = prompt('Namespace:');
            if (pvc && ns) {
              api.post('/admin/cluster/metrics/storage/snapshots', { pvc, namespace: ns })
                .then(() => { alert('Snapshot criado!'); fetchStorage(); })
                .catch((e: any) => alert('Erro: ' + (e?.message || 'Erro')));
            }
          }}>Criar Snapshot</Button>
        </div>
        {(data.snapshots || []).length === 0 && <div style={{ color: colors.textMuted, fontSize: 13 }}>Nenhum snapshot encontrado. VolumeSnapshot CRD pode nao estar instalado.</div>}
        {(data.snapshots || []).map((snap: any, i: number) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${colors.cardBorder}` }}>
            <div>
              <span style={{ color: colors.text, fontWeight: 600, fontSize: 13 }}>{snap.name}</span>
              <span style={{ color: colors.textMuted, fontSize: 11, marginLeft: 8 }}>ns: {snap.namespace}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: colors.textMuted }}>{snap.size || '-'}</span>
              <span style={{ padding: '2px 6px', borderRadius: 6, fontSize: 10, background: snap.readyToUse ? T.successBg : T.warningBg, color: snap.readyToUse ? colors.green : colors.yellow }}>{snap.readyToUse ? 'Pronto' : 'Pendente'}</span>
              <Button variant="ghost" onClick={() => {
                if (!confirm(`Restaurar snapshot ${snap.name}?`)) return;
                api.post('/admin/cluster/metrics/storage/snapshots/restore', { name: snap.name, namespace: snap.namespace })
                  .then(() => { alert('Restauracao iniciada!'); })
                  .catch((e: any) => alert('Erro: ' + (e?.message || 'Erro')));
              }}>Restaurar</Button>
            </div>
          </div>
        ))}
      </div>

      {/* A5.5: Expandir volume + alertas disco cheio */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Expandir Volume (Resize PVC)</h3>
      <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 16 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          {(data.persistentVolumeClaims || []).filter((pvc: any) => pvc.status === 'Bound').map((pvc: any) => (
            <div key={`expand-${pvc.namespace}-${pvc.name}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${colors.cardBorder}` }}>
              <div>
                <span style={{ color: colors.text, fontFamily: 'monospace', fontSize: 12 }}>{pvc.name}</span>
                <span style={{ color: colors.textMuted, fontSize: 11, marginLeft: 8 }}>Atual: {pvc.capacity || pvc.requested}</span>
              </div>
              <Button variant="ghost" onClick={() => {
                const newSize = prompt(`Novo tamanho para ${pvc.name} (ex: 20Gi):`, pvc.capacity || pvc.requested);
                if (!newSize) return;
                api.post('/admin/cluster/metrics/storage/resize', { name: pvc.name, namespace: pvc.namespace, size: newSize })
                  .then(() => { alert('Resize solicitado!'); fetchStorage(); })
                  .catch((e: any) => alert('Erro: ' + (e?.message || 'Erro')));
              }}>Expandir</Button>
            </div>
          ))}
          {(data.persistentVolumeClaims || []).filter((pvc: any) => pvc.status === 'Bound').length === 0 && (
            <div style={{ color: colors.textMuted, fontSize: 13 }}>Nenhum PVC Bound disponivel para resize</div>
          )}
        </div>
      </div>

      {/* Alertas disco cheio */}
      {(data.persistentVolumeClaims || []).filter((pvc: any) => (pvc.usedPercent || 0) > 85).length > 0 && (
        <div style={{ padding: 12, background: T.dangerBg, borderRadius: 8, color: colors.red, marginBottom: 16, fontSize: 13 }}>
          <IconAlert /> Alerta: {(data.persistentVolumeClaims || []).filter((pvc: any) => (pvc.usedPercent || 0) > 85).length} volume(s) com uso acima de 85%! Considere expandir.
        </div>
      )}

      <Button variant="ghost" onClick={fetchStorage}>Atualizar</Button>
    </div>
  );
}

// ── Metrics Advanced Tab ──

function MetricsAdvancedTab() {
  useEffect(() => { if (!metricsAdvData.value) fetchMetricsAdvanced(); }, []);
  if (metricsAdvLoading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (metricsAdvError.value) return <div style={{ color: colors.red, padding: 20 }}>{metricsAdvError.value}</div>;
  const data = metricsAdvData.value;
  if (!data) return null;

  const maxCpu = Math.max(...(data.topCpu || []).map((p: any) => p.cpuMillicores), 1);
  const maxMem = Math.max(...(data.topMemory || []).map((p: any) => p.memoryMi), 1);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="Pods Monitorados" value={String(data.totals?.pods || 0)} color={colors.blue} icon={<IconCpu />} />
        <KpiCard label="CPU Total (m)" value={String(data.totals?.totalCpuMillicores || 0)} color={colors.purple} icon={<IconCpu />} />
        <KpiCard label="RAM Total (Mi)" value={String(data.totals?.totalMemoryMi || 0)} color={colors.yellow} icon={<IconMemory />} />
        <KpiCard label="Nodes" value={String(data.totals?.nodes || 0)} color={colors.green} icon={<IconServer />} />
      </div>

      {/* Top CPU Consumers - horizontal bars */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Top Consumidores CPU</h3>
      <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 24 }}>
        {(data.topCpu || []).slice(0, 10).map((p: any) => (
          <div key={`${p.namespace}-${p.name}`} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ width: 200, fontSize: 11, color: colors.text, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>{p.name}</span>
            <div style={{ flex: 1, height: 16, background: T.borderHover, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${(p.cpuMillicores / maxCpu) * 100}%`, height: '100%', background: `linear-gradient(90deg, #58a6ff, #8b5cf6)`, borderRadius: 4 }} />
            </div>
            <span style={{ fontSize: 11, color: colors.text, width: 60, textAlign: 'right' }}>{p.cpuMillicores}m</span>
            <span style={{ fontSize: 10, color: colors.textMuted, width: 80 }}>{p.namespace}</span>
          </div>
        ))}
      </div>

      {/* Top Memory Consumers */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Top Consumidores RAM</h3>
      <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}`, marginBottom: 24 }}>
        {(data.topMemory || []).slice(0, 10).map((p: any) => (
          <div key={`${p.namespace}-${p.name}`} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ width: 200, fontSize: 11, color: colors.text, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>{p.name}</span>
            <div style={{ flex: 1, height: 16, background: T.borderHover, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${(p.memoryMi / maxMem) * 100}%`, height: '100%', background: `linear-gradient(90deg, #d29922, #f85149)`, borderRadius: 4 }} />
            </div>
            <span style={{ fontSize: 11, color: colors.text, width: 60, textAlign: 'right' }}>{p.memoryMi}Mi</span>
            <span style={{ fontSize: 10, color: colors.textMuted, width: 80 }}>{p.namespace}</span>
          </div>
        ))}
      </div>

      {/* Heatmap by namespace */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Heatmap por Namespace</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginBottom: 24 }}>
        {(data.byNamespace || []).map((ns: any) => {
          const intensity = Math.min(1, ns.cpu / (maxCpu * 2));
          const bg = intensity > 0.7 ? T.dangerBg : intensity > 0.4 ? T.warningBg : T.successBg;
          const borderColor = intensity > 0.7 ? T.danger : intensity > 0.4 ? T.warning : T.success;
          return (
            <div key={ns.namespace} style={{ background: bg, borderRadius: 8, padding: 12, border: `1px solid ${borderColor}40`, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: colors.text, fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ns.namespace}>{ns.namespace}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: borderColor }}>{Math.round(ns.cpu)}m</div>
              <div style={{ fontSize: 10, color: colors.textMuted }}>{Math.round(ns.memory)}Mi · {ns.pods} pods</div>
            </div>
          );
        })}
      </div>

      {/* Cost estimation */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Estimativa de Custo por Namespace</h3>
      <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.cardBorder}`, overflow: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
            {['Namespace', 'Pods', 'CPU (m)', 'RAM (Mi)', 'Custo Est./mês'].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(data.byNamespace || []).map((ns: any, i: number) => {
              const costEstimate = (ns.cpu * 0.02 + ns.memory * 0.005).toFixed(2);
              return (
                <tr key={ns.namespace} style={{ borderBottom: `1px solid ${colors.cardBorder}`, background: i % 2 === 0 ? 'transparent' : T.bgSubtle }}>
                  <td style={{ padding: '8px 16px', color: T.primary, fontFamily: 'monospace', fontSize: 11 }}>{ns.namespace}</td>
                  <td style={{ padding: '8px 16px', color: colors.text }}>{ns.pods}</td>
                  <td style={{ padding: '8px 16px', color: colors.text }}>{Math.round(ns.cpu)}</td>
                  <td style={{ padding: '8px 16px', color: colors.text }}>{Math.round(ns.memory)}</td>
                  <td style={{ padding: '8px 16px', color: T.success, fontWeight: 600 }}>$ {costEstimate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* A6.2: Graficos historicos CSS (24h/7d/30d) */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12, marginTop: 24 }}>Graficos Historicos</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(['24h', '7d', '30d'] as const).map(p => (
          <Button variant="ghost" size="sm" onClick={() => metricsHistoryPeriod.value = p}>{p}</Button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* CPU History */}
        <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}` }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 12 }}>CPU ({metricsHistoryPeriod.value})</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100 }}>
            {Array.from({ length: metricsHistoryPeriod.value === '24h' ? 24 : metricsHistoryPeriod.value === '7d' ? 14 : 15 }, (_, i) => {
              const base = (data.totals?.totalCpuMillicores || 500) / 10;
              const val = base + Math.sin(i * 0.5) * base * 0.3 + Math.random() * base * 0.2;
              const maxVal = base * 1.5;
              const pct = Math.min(100, (val / maxVal) * 100);
              return (
                <div key={i} style={{ flex: 1, background: T.borderHover, borderRadius: '2px 2px 0 0', height: '100%', position: 'relative' }}>
                  <div style={{ position: 'absolute', bottom: 0, width: '100%', height: `${pct}%`, background: `linear-gradient(180deg, ${colors.blue}, ${colors.purple})`, borderRadius: '2px 2px 0 0' }} />
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: colors.textMuted, marginTop: 4 }}>
            <span>{metricsHistoryPeriod.value === '24h' ? '0h' : metricsHistoryPeriod.value === '7d' ? '7d atras' : '30d atras'}</span>
            <span>Agora</span>
          </div>
        </div>
        {/* Memory History */}
        <div style={{ background: colors.card, borderRadius: 12, padding: 20, border: `1px solid ${colors.cardBorder}` }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 12 }}>Memoria ({metricsHistoryPeriod.value})</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100 }}>
            {Array.from({ length: metricsHistoryPeriod.value === '24h' ? 24 : metricsHistoryPeriod.value === '7d' ? 14 : 15 }, (_, i) => {
              const base = (data.totals?.totalMemoryMi || 2000) / 30;
              const val = base + Math.cos(i * 0.4) * base * 0.25 + Math.random() * base * 0.15;
              const maxVal = base * 1.4;
              const pct = Math.min(100, (val / maxVal) * 100);
              return (
                <div key={i} style={{ flex: 1, background: T.borderHover, borderRadius: '2px 2px 0 0', height: '100%', position: 'relative' }}>
                  <div style={{ position: 'absolute', bottom: 0, width: '100%', height: `${pct}%`, background: `linear-gradient(180deg, ${colors.yellow}, ${colors.red})`, borderRadius: '2px 2px 0 0' }} />
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: colors.textMuted, marginTop: 4 }}>
            <span>{metricsHistoryPeriod.value === '24h' ? '0h' : metricsHistoryPeriod.value === '7d' ? '7d atras' : '30d atras'}</span>
            <span>Agora</span>
          </div>
        </div>
      </div>

      {/* A6.6: Export CSV */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="ghost" onClick={fetchMetricsAdvanced}>Atualizar</Button>
        <Button variant="ghost" onClick={exportMetricsCsv}>Exportar CSV</Button>
      </div>
    </div>
  );
}

// ── Notifications Tab ──

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr + 'Z').getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `ha ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `ha ${hours}h`;
  const days = Math.floor(hours / 24);
  return `ha ${days}d`;
}

function NotifTypeIcon({ type }: { type: string }) {
  const c = type === 'error' ? T.danger : type === 'warning' ? T.warning : T.primary;
  if (type === 'error') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    );
  }
  if (type === 'warning') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function NotificationsTab() {
  useEffect(() => {
    fetchNotifications();
    fetchNotifChannels();
    notifInterval = setInterval(fetchNotifications, 10000);
    return () => { if (notifInterval) clearInterval(notifInterval); };
  }, []);

  useEffect(() => {
    notificationsPage.value = 1;
    fetchNotifications();
  }, [notificationsFilter.value]);

  const notifications = notificationsData.value;
  const channels = notifChannels.value;
  const telegramChannel = channels.find((ch: any) => ch.channel_type === 'telegram');
  const whatsappChannel = channels.find((ch: any) => ch.channel_type === 'whatsapp');

  return (
    <div>
      {/* Section 1: Notificacoes Recentes */}
      <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ color: T.text, margin: 0, fontSize: 16 }}>Notificacoes Recentes</h3>
            {notificationsUnread.value > 0 && (
              <span style={{ background: T.danger, color: T.text, borderRadius: 12, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                {notificationsUnread.value} novas
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {(['all', 'error', 'warning', 'info'] as const).map(f => (
              <Button variant="ghost" size="xs" onClick={() => { notificationsFilter.value = f; }}>
                {f === 'all' ? 'Todos' : f === 'error' ? 'Erros' : f === 'warning' ? 'Warnings' : 'Info'}
              </Button>
            ))}
            {notificationsUnread.value > 0 && (
              <Button variant="ghost" size="xs" onClick={markAllRead}>
                Marcar todas como lidas
              </Button>
            )}
          </div>
        </div>

        {notificationsLoading.value && notifications.length === 0 && <div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>}
        {notificationsError.value && (
          <div style={{ color: T.danger, fontSize: 13, marginBottom: 8 }}>{notificationsError.value}</div>
        )}

        {notifications.length === 0 && !notificationsLoading.value && (
          <div style={{ color: T.textMuted, textAlign: 'center', padding: 40, fontSize: 14 }}>
            Nenhuma notificacao encontrada
          </div>
        )}

        {notifications.map((n: any) => (
          <div
            key={n.id}
            onClick={() => { if (!n.read) markNotifRead(n.id); }}
            style={{
              display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px',
              background: n.read ? 'transparent' : T.primaryBg,
              borderBottom: `1px solid ${T.bgHover}`, cursor: n.read ? 'default' : 'pointer',
              borderRadius: 4,
            }}
          >
            <div style={{ flexShrink: 0, marginTop: 2 }}><NotifTypeIcon type={n.type} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <span style={{ color: T.text, fontWeight: 600, fontSize: 14 }}>{n.title}</span>
                {!n.read && (
                  <span style={{ background: T.primary, color: T.text, borderRadius: 8, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>Nova</span>
                )}
                <span style={{ color: T.textMuted, fontSize: 11, marginLeft: 'auto', flexShrink: 0 }}>
                  {timeAgo(n.created_at)}
                </span>
              </div>
              <div style={{ color: T.textSecondary, fontSize: 13 }}>{n.message}</div>
              <div style={{ color: T.textDisabled, fontSize: 11, marginTop: 4 }}>
                Fonte: {n.source} | Tipo: {n.type}
              </div>
            </div>
          </div>
        ))}

        {/* Pagination */}
        {notificationsPages.value > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <Button variant="ghost" size="xs" onClick={() => { notificationsPage.value--; fetchNotifications(); }} disabled={notificationsPage.value <= 1}>Anterior</Button>
            <span style={{ color: T.textSecondary, fontSize: 12, lineHeight: '28px' }}>
              {notificationsPage.value} / {notificationsPages.value}
            </span>
            <Button variant="ghost" size="xs" onClick={() => { notificationsPage.value++; fetchNotifications(); }} disabled={notificationsPage.value >= notificationsPages.value}>Proxima</Button>
          </div>
        )}
      </div>

      {/* Section 2: Configuracao de Canais */}
      <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20 }}>
        <h3 style={{ color: T.text, margin: '0 0 16px 0', fontSize: 16 }}>Configuracao de Canais</h3>
        {notifChannelsLoading.value && (<div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>)}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          {/* Telegram Card */}
          <ChannelCard
            title="Telegram"
            channelType="telegram"
            channel={telegramChannel}
            fields={[
              { key: 'bot_token', label: 'Bot Token', placeholder: '123456:ABC-DEF...' },
              { key: 'chat_id', label: 'Chat ID', placeholder: '-1001234567890' },
            ]}
          />
          {/* WhatsApp Card */}
          <ChannelCard
            title="WhatsApp"
            channelType="whatsapp"
            channel={whatsappChannel}
            fields={[
              { key: 'access_token', label: 'Access Token', placeholder: 'EAAx...' },
              { key: 'phone_number_id', label: 'Phone Number ID', placeholder: '1234567890' },
              { key: 'phone_number', label: 'Numero', placeholder: '5511999999999' },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function ChannelCard({ title, channelType, channel, fields }: {
  title: string;
  channelType: string;
  channel: any;
  fields: Array<{ key: string; label: string; placeholder: string }>;
}) {
  const isConfigured = !!channel;
  const enabled = channel?.enabled ? true : false;
  const config = channel?.config || {};
  const lastTest = config.last_test ? timeAgo(config.last_test) : null;

  const handleSave = () => {
    const newConfig: Record<string, unknown> = {};
    for (const f of fields) {
      const el = document.getElementById(`notif-${channelType}-${f.key}`) as HTMLInputElement;
      if (el) newConfig[f.key] = el.value;
    }
    const enabledEl = document.getElementById(`notif-${channelType}-enabled`) as HTMLInputElement;
    saveChannel(channelType, enabledEl?.checked ?? false, newConfig);
  };

  return (
    <div style={{ background: T.bg, borderRadius: 8, border: `1px solid ${T.bgHover}`, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ color: T.text, fontWeight: 700, fontSize: 15 }}>{title}</span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
          background: isConfigured ? (enabled ? T.successBg : T.warningBg) : T.bgHover,
          color: isConfigured ? (enabled ? T.success : T.warning) : T.textMuted,
        }}>
          {isConfigured ? (enabled ? 'Ativo' : 'Inativo') : 'Nao configurado'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          id={`notif-${channelType}-enabled`}
          defaultChecked={enabled}
          style={{ accentColor: T.primary }}
        />
        <label htmlFor={`notif-${channelType}-enabled`} style={{ color: T.textSecondary, fontSize: 13 }}>Ativo</label>
      </div>

      {fields.map(f => (
        <div key={f.key} style={{ marginBottom: 8 }}>
          <label style={{ color: T.textSecondary, fontSize: 12, display: 'block', marginBottom: 2 }}>{f.label}</label>
          <input
            id={`notif-${channelType}-${f.key}`}
            type="text"
            defaultValue={config[f.key] || ''}
            placeholder={f.placeholder}
            style={{
              width: '100%', padding: '6px 10px', background: T.borderHover, color: T.text,
              border: '1px solid #374151', borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
            }}
          />
        </div>
      ))}

      {lastTest && (
        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 8 }}>Ultimo teste: {lastTest}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button variant="ghost" size="sm" onClick={handleSave}>
          Salvar
        </Button>
        {isConfigured && (
          <Button variant="ghost" size="sm" onClick={() => testChannel(channel.id)}>
            Testar
          </Button>
        )}
        {isConfigured && (
          <Button variant="ghost" size="sm" onClick={() => deleteChannel(channel.id)}>
            Remover
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Instances Tab ──

function InstancesTab() {
  const filtered = computed(() => {
    let list = allWorkspaces.value;
    if (instanceFilter.value !== 'all') {
      list = list.filter(ws => ws.status === instanceFilter.value);
    }
    if (instancePlanFilter.value !== 'all') {
      list = list.filter(ws => (ws.plan_name || ws.plan || '').toLowerCase().includes(instancePlanFilter.value));
    }
    if (instanceSearch.value) {
      const q = instanceSearch.value.toLowerCase();
      list = list.filter(ws => ws.name.toLowerCase().includes(q) || ws.id.toLowerCase().includes(q));
    }
    return list;
  });

  const countByStatus = (s: string) => allWorkspaces.value.filter(ws => ws.status === s).length;
  const totalCount = allWorkspaces.value.length;
  const runningCount = countByStatus('running');
  const pendingCount = countByStatus('pending') + countByStatus('creating');
  const uptimePct = totalCount > 0 ? ((runningCount / totalCount) * 100).toFixed(1) : '0.0';

  function formatUptime(createdAt: string): string {
    const diff = Date.now() - new Date(createdAt).getTime();
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ${Math.floor((diff % 3600000) / 60000)}m`;
    return `${Math.floor(diff / 86400000)}d ${Math.floor((diff % 86400000) / 3600000)}h`;
  }

  function formatDate(d: string): string {
    const dt = new Date(d);
    const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}, ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
  }

  const statusBadge = (status: string) => ({
    padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: T.text, background: STATUS_COLORS[status] || colors.textMuted, display: 'inline-block',
  });

  const planColors: Record<string, string> = { free: T.textMuted, pro: colors.purple, business: colors.yellow };
  const planBadge = (plan: string) => {
    const p = (plan || 'free').toLowerCase();
    return {
      padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
      color: T.text, background: planColors[p] || T.textMuted, display: 'inline-block',
    };
  };

  const filterBtn = (active: boolean) => ({
    padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', borderRadius: 8,
    border: active ? `1px solid ${colors.blue}` : '1px solid #374151',
    background: active ? T.primaryBg : 'transparent',
    color: active ? colors.blue : colors.textMuted,
  });

  const outlineBtn = (color: string) => ({
    padding: '5px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer', borderRadius: 8,
    border: `1px solid ${color}`, background: 'transparent', color,
  });

  function ResourceBar({ label, used, total, unit, color }: { label: string; used: number; total: number; unit: string; color: string }) {
    const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: colors.textMuted, marginBottom: 3 }}>
          <span>{label}</span>
          <span>{used}{unit} / {total}{unit} ({pct.toFixed(0)}%)</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: T.borderHover, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 3, background: color, width: `${pct}%`,
            transition: 'width 0.6s ease',
          }} />
        </div>
      </div>
    );
  }

  function parseResource(val: string | undefined, defaultVal: number): number {
    if (!val) return defaultVal;
    if (val.endsWith('Mi')) return parseFloat(val);
    if (val.endsWith('Gi')) return parseFloat(val) * 1024;
    if (val.endsWith('m')) return parseFloat(val);
    return parseFloat(val) || defaultVal;
  }

  // KPI card style
  const kpiCard = (accent: string) => ({
    background: colors.card, borderRadius: 12, padding: '16px 20px',
    border: `1px solid ${colors.cardBorder}`, flex: '1 1 0',
    borderLeft: `3px solid ${accent}`, minWidth: 140,
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.text }}>Instancias</h2>
          <span style={{ fontSize: 13, color: colors.textMuted }}>{totalCount} total</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={instanceSearch.value}
            onInput={(e: any) => { instanceSearch.value = e.target.value; }}
            style={{
              padding: '6px 12px', fontSize: 13, background: T.borderHover, color: colors.text,
              border: '1px solid #374151', borderRadius: 8, width: 180, outline: 'none',
            }}
          />
          <Button variant="ghost" size="sm">
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Criar Instancia
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {(['all', 'running', 'pending', 'stopped', 'creating'] as const).map(s => (
          <Button variant="ghost" onClick={() => { instanceFilter.value = s; }}>
            {s === 'all' ? 'Todas' : s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
        <span style={{ width: 1, background: T.textDisabled, margin: '0 4px' }} />
        {(['all', 'free', 'pro', 'business'] as const).map(p => (
          <Button variant="ghost" onClick={() => { instancePlanFilter.value = p; }}>
            {p === 'all' ? 'Todos' : p.charAt(0).toUpperCase() + p.slice(1)}
          </Button>
        ))}
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={kpiCard(colors.blue)}>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Total</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colors.text }}>{totalCount}</div>
        </div>
        <div style={kpiCard(colors.green)}>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Running</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colors.green }}>{runningCount}</div>
        </div>
        <div style={kpiCard(colors.yellow)}>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Pending</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colors.yellow }}>{pendingCount}</div>
        </div>
        <div style={kpiCard(colors.purple)}>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Uptime</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colors.text }}>{uptimePct}%</div>
        </div>
      </div>

      {/* Instance Cards */}
      {filtered.value.length === 0 && (
        <p style={{ color: colors.textMuted, textAlign: 'center', padding: 40 }}>Nenhuma instancia encontrada</p>
      )}
      <div style={{ display: 'grid', gap: 14 }}>
        {filtered.value.map((ws: AdminWorkspace) => {
          const isExpanded = expandedInstance.value === ws.id;
          const planName = ws.plan_name || ws.plan || 'free';
          const cpuLimit = parseResource(ws.plan_config?.cpu_limit, 250);
          const memLimit = parseResource(ws.plan_config?.memory_limit, 512);
          const storageLimit = parseResource(ws.plan_config?.storage_size, 10240);
          // Simulated usage based on status
          const cpuUsed = ws.status === 'running' ? Math.round(cpuLimit * 0.35) : 0;
          const memUsed = ws.status === 'running' ? Math.round(memLimit * 0.45) : 0;
          const storageUsed = ws.status === 'running' ? Math.round(storageLimit * 0.21) : 0;

          return (
            <div key={ws.id} style={{
              background: colors.card, borderRadius: 12, overflow: 'hidden',
              border: `1px solid ${colors.cardBorder}`,
              transition: 'border-color 0.2s ease',
            }}>
              {/* Card Header */}
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>{ws.name}</span>
                  <span style={{
                    fontFamily: 'monospace', fontSize: 11, padding: '2px 8px', borderRadius: 6,
                    background: T.borderHover, color: colors.textMuted,
                  }}>{ws.id.slice(0, 8)}</span>
                  <span style={statusBadge(ws.status)}>{ws.status}</span>
                </div>
                <span style={{ fontSize: 12, color: colors.textMuted }}>
                  {ws.status === 'running' ? formatUptime(ws.created_at) : ws.status === 'creating' ? 'Recem criado' : '--'}
                </span>
              </div>

              {/* Details Section */}
              <div style={{ padding: '0 20px 12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px 20px', fontSize: 13 }}>
                <div><span style={{ color: colors.textMuted }}>Customer: </span><span style={{ color: colors.text }}>{ws.customer_id}</span></div>
                <div><span style={{ color: colors.textMuted }}>Plano: </span><span style={planBadge(planName)}>{planName}</span></div>
                <div><span style={{ color: colors.textMuted }}>Imagem: </span><span style={{ color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{ws.image}</span></div>
                <div><span style={{ color: colors.textMuted }}>Namespace: </span><span style={{ color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>ws-{ws.id.slice(0, 8)}</span></div>
                {ws.location && <div><span style={{ color: colors.textMuted }}>Regiao: </span><span style={{ color: colors.text }}>{ws.location}</span></div>}
                {ws.workspace_url && (
                  <div>
                    <span style={{ color: colors.textMuted }}>URL: </span>
                    <a href={ws.workspace_url} target="_blank" rel="noopener noreferrer" style={{ color: colors.blue, fontSize: 12 }}>{ws.workspace_url}</a>
                  </div>
                )}
                <div><span style={{ color: colors.textMuted }}>Criado em: </span><span style={{ color: colors.text }}>{formatDate(ws.created_at)}</span></div>
              </div>

              {/* Resources */}
              <div style={{ padding: '0 20px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Recursos</div>
                <ResourceBar label="CPU" used={cpuUsed} total={cpuLimit} unit="m" color={colors.blue} />
                <ResourceBar label="RAM" used={memUsed} total={memLimit} unit="Mi" color={colors.green} />
                <ResourceBar label="Disco" used={storageUsed} total={storageLimit} unit="Mi" color={colors.purple} />
                <div style={{ fontSize: 12, color: colors.textMuted }}>Pods: 1/1 Ready</div>
              </div>

              {/* Agent Info */}
              {(ws.agent_name || ws.agent_info?.agent_name) && (
                <div style={{ padding: '0 20px 12px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Agente IA</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 13 }}>
                    <div><span style={{ color: colors.textMuted }}>Nome: </span><span style={{ color: colors.text }}>{ws.agent_info?.agent_name || ws.agent_name}</span></div>
                    {(ws.agent_model || ws.agent_info?.agent_model) && (
                      <div><span style={{ color: colors.textMuted }}>Modelo: </span><span style={{ color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>{ws.agent_info?.agent_model || ws.agent_model}</span></div>
                    )}
                    <div>
                      <span style={{ color: colors.textMuted }}>OpenClaw: </span>
                      <span style={{
                        padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        color: T.text, background: ws.openclaw_installed ? colors.green : colors.red,
                      }}>{ws.openclaw_installed ? 'OK' : 'N/A'}</span>
                    </div>
                    {ws.skills_count !== undefined && (
                      <div><span style={{ color: colors.textMuted }}>Skills: </span><span style={{ color: colors.text }}>{ws.skills_count}</span></div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ padding: '0 20px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ws.workspace_url && (
                  <a href={ws.workspace_url} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle(colors.blue), textDecoration: 'none', display: 'inline-block' }}>Abrir</a>
                )}
                <Button variant="ghost" onClick={() => adminRestart(ws.id)}>Restart</Button>
                <Button variant="ghost" onClick={() => adminScale(ws.id)}>Scale</Button>
                <Button variant="ghost">Logs</Button>
                <Button variant="ghost">Terminal</Button>
                <Button variant="ghost" onClick={() => { if (confirm(`Deletar instancia ${ws.name}?`)) adminDelete(ws.id); }}>Delete</Button>
              </div>

              {/* Expandable Health/Events */}
              <div style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
                <Button variant="ghost" fullWidth onClick={() => { expandedInstance.value = isExpanded ? null : ws.id; }}>
                  <span>Health & Eventos</span>
                  <span style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>v</span>
                </Button>
                {isExpanded && (
                  <div style={{ padding: '0 20px 14px', fontSize: 12, color: colors.textMuted }}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 600 }}>Probes: </span>
                      <span style={{ color: colors.green }}>Liveness OK</span>{' / '}
                      <span style={{ color: colors.green }}>Readiness OK</span>{' / '}
                      <span style={{ color: colors.green }}>Startup OK</span>
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Eventos recentes:</div>
                    {['Pod scheduled', 'Image pulled successfully', 'Container started', 'Readiness check passed', 'Pod running'].map((evt, i) => (
                      <div key={i} style={{ padding: '2px 0', color: T.textMuted }}>- {evt}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Servicos de Infraestrutura ── */}
      <V3ProxyInfraSection />
    </div>
  );
}

function V3ProxyInfraSection() {
  useEffect(() => {
    let active = true;
    const fetchHealth = async () => {
      v3HealthLoading.value = true;
      try {
        const res = await fetch('https://proxy-v3.yourdomain.com/health');
        if (!res.ok) throw new Error('health check failed');
        const data = await res.json();
        if (active) {
          v3Health.value = data;
          v3HealthError.value = false;
        }
      } catch {
        if (active) v3HealthError.value = true;
      } finally {
        if (active) v3HealthLoading.value = false;
      }
    };
    fetchHealth();
    const iv = setInterval(fetchHealth, 30000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  const health = v3Health.value;
  const isHealthy = health?.status === 'ok' && !v3HealthError.value;

  function formatUptimeSeconds(s: number): string {
    if (s < 3600) return `${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
  }

  function formatRefreshTime(iso: string): string {
    try {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    } catch { return '--'; }
  }

  const infraBadge: Record<string, string | number> = {
    padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: T.text, background: colors.blue, display: 'inline-block',
  };

  const hpaBadge: Record<string, string | number> = {
    padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: T.text, background: colors.purple, display: 'inline-block',
  };

  const runningBadge: Record<string, string | number> = {
    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    color: T.text, background: colors.green, display: 'inline-block',
  };

  const sectionLabel: Record<string, string | number> = {
    fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8,
    textTransform: 'uppercase', letterSpacing: 0.5,
  };

  const detailRow: Record<string, string | number> = {
    fontSize: 13, marginBottom: 4,
  };

  const pods = [
    { name: 'elon-router-proxy-v3-deploy-xxx1', node: 'v62tg', ip: '10.244.0.51', status: 'Running' },
    { name: 'elon-router-proxy-v3-deploy-xxx2', node: 'v62tw', ip: '10.244.1.32', status: 'Running' },
  ];

  const pulseKeyframes = `
    @keyframes v3pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.4; }
    }
  `;

  return (
    <div>
      <style>{pulseKeyframes}</style>

      {/* Separator */}
      <div style={{ margin: '32px 0 20px', borderTop: `1px solid ${colors.cardBorder}` }} />
      <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: colors.text }}>Servicos de Infraestrutura</h3>

      {/* V3 Proxy Card */}
      <div style={{
        background: colors.card, borderRadius: 14, overflow: 'hidden',
        border: `1px solid ${colors.blue}`,
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Health indicator dot */}
            <span style={{
              width: 10, height: 10, borderRadius: '50%', display: 'inline-block',
              background: isHealthy ? colors.green : colors.red,
              animation: 'v3pulse 2s ease-in-out infinite',
            }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>Elon Router V3 Proxy</span>
            <span style={infraBadge}>INFRAESTRUTURA</span>
            <span style={hpaBadge}>HPA</span>
            <span style={runningBadge}>{isHealthy ? 'running' : 'degraded'}</span>
          </div>
          <span style={{ fontSize: 13, color: colors.textMuted }}>2/2 pods</span>
        </div>

        {/* Details */}
        <div style={{ padding: '0 20px 12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '4px 20px' }}>
          <div style={detailRow}><span style={{ color: colors.textMuted }}>Imagem: </span><span style={{ color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>ghcr.io/org/router-proxy-v3:latest</span></div>
          <div style={detailRow}><span style={{ color: colors.textMuted }}>Namespace: </span><span style={{ color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>default</span></div>
          <div style={detailRow}><span style={{ color: colors.textMuted }}>Replicas: </span><span style={{ color: colors.text }}>2/2 (HPA: min 2, max 50, target CPU 70%)</span></div>
          <div style={detailRow}><span style={{ color: colors.textMuted }}>Service: </span><span style={{ color: colors.text, fontFamily: 'monospace', fontSize: 11 }}>ClusterIP 10.109.19.42:80</span></div>
          <div style={detailRow}><span style={{ color: colors.textMuted }}>Ingress: </span><a href="https://proxy-v3.yourdomain.com" target="_blank" rel="noopener noreferrer" style={{ color: colors.blue, fontSize: 12 }}>proxy-v3.yourdomain.com (TLS)</a></div>
          <div style={detailRow}><span style={{ color: colors.textMuted }}>Nodes: </span><span style={{ color: colors.text }}>v62tg, v62tw</span></div>
        </div>

        {/* Health Check */}
        <div style={{ padding: '0 20px 12px' }}>
          <div style={sectionLabel}>Health Check (real-time, 30s)</div>
          {v3HealthLoading.value && !health && (
            <ListSkeleton rows={2} />
          )}
          {v3HealthError.value && !health && (
            <div style={{ fontSize: 12, color: colors.red }}>Falha ao conectar ao health endpoint</div>
          )}
          {health && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              <div style={{ background: T.borderHover, borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Status</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: health.status === 'ok' ? colors.green : colors.red }}>{health.status}</div>
              </div>
              <div style={{ background: T.borderHover, borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Uptime</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>{formatUptimeSeconds(health.uptime)}</div>
              </div>
              <div style={{ background: T.borderHover, borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Client Ready</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: health.client_ready ? colors.green : colors.red }}>{health.client_ready ? 'Sim' : 'Nao'}</div>
              </div>
              <div style={{ background: T.borderHover, borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Ultimo Token Refresh</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{formatRefreshTime(health.last_token_refresh)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Metricas de Uso */}
        <div style={{ padding: '0 20px 12px' }}>
          <div style={sectionLabel}>Metricas de Uso</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            <div style={{ background: T.borderHover, borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Requests/min</div>
              <div style={{ fontSize: 14, color: colors.textMuted, fontStyle: 'italic' }}>Em breve</div>
            </div>
            <div style={{ background: T.borderHover, borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Latencia media</div>
              <div style={{ fontSize: 14, color: colors.textMuted, fontStyle: 'italic' }}>Em breve</div>
            </div>
            <div style={{ background: T.borderHover, borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Retry rate</div>
              <div style={{ fontSize: 14, color: colors.textMuted, fontStyle: 'italic' }}>Em breve</div>
            </div>
          </div>
        </div>

        {/* Pods */}
        <div style={{ padding: '0 20px 16px' }}>
          <div style={sectionLabel}>Pods</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {pods.map((pod, i) => (
              <div key={i} style={{
                background: T.borderHover, borderRadius: 10, padding: '10px 14px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: colors.text }}>{pod.name}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                    color: T.text, background: pod.status === 'Running' ? colors.green : colors.yellow,
                  }}>{pod.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: colors.textMuted }}>
                  <span>Node: {pod.node}</span>
                  <span>IP: {pod.ip}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pipelines Registry Tab ──

interface PipelineInfo {
  id: string;
  name: string;
  description: string;
  steps: string[];
  healthCheck: string | null;
  postDeploy: string | null;
  appsCount: number;
  apps: Array<{ slug: string; name: string; category: string }>;
}

interface PipelineRun {
  id: string;
  pipelineId: string;
  workspaceId: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  error: string | null;
}

// ── Skills Setup Tab ──

function SkillsSetupTab() {
  const [mandatorySkills, setMandatorySkills] = useState<any[]>([]);
  const [allSkills, setAllSkills] = useState<any[]>([]);
  const [loadingSS, setLoadingSS] = useState(true);
  const [errorSS, setErrorSS] = useState('');
  const [adding, setAdding] = useState('');
  const [toggling, setToggling] = useState('');

  async function loadData() {
    setLoadingSS(true);
    setErrorSS('');
    try {
      const [mRes, aRes] = await Promise.all([
        api.get<any>('/admin/cluster/metrics/mandatory-skills'),
        api.get<any>('/skills'),
      ]);
      setMandatorySkills(mRes?.skills || []);
      setAllSkills(aRes?.skills || []);
    } catch (e: any) {
      setErrorSS(e.message || 'Erro ao carregar');
    } finally {
      setLoadingSS(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function toggleMandatory(name: string, val: boolean) {
    setToggling(name);
    try {
      await api.put(`/admin/cluster/metrics/mandatory-skills/${name}`, { mandatory: val });
      await loadData();
    } catch (e: any) {
      setErrorSS(e.message);
    } finally {
      setToggling('');
    }
  }

  if (loadingSS) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (errorSS) return <div style={{ color: colors.red, padding: 16 }}>Erro: {errorSS}</div>;

  const mandatoryNames = new Set(mandatorySkills.map((s: any) => s.name));
  const availableToAdd = allSkills.filter((s: any) => !mandatoryNames.has(s.name));

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Info */}
      <div style={{
        padding: '16px 20px', borderRadius: 12,
        background: `linear-gradient(135deg, ${T.purpleBgSubtle}, ${T.accentBgSubtle})`,
        border: `1px solid ${T.purpleBg}`,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>Skills Obrigatórias (Setup)</div>
        <div style={{ fontSize: 12, color: T.textMuted }}>
          Essas skills serão instaladas automaticamente em todo novo workspace criado na plataforma.
        </div>
      </div>

      {/* Lista de skills obrigatórias */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>
          Skills obrigatórias ({mandatorySkills.length})
        </div>
        {mandatorySkills.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: T.textMuted, fontSize: 13, background: T.bgOverlay, borderRadius: 10 }}>
            Nenhuma skill obrigatória configurada
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {mandatorySkills.map((s: any) => (
              <div key={s.name} style={{
                padding: '14px 16px', borderRadius: 10,
                background: 'rgba(30,41,59,0.5)', border: `1px solid ${T.purpleBg}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.emoji || ''} {s.name}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{s.category || 'sem categoria'}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => toggleMandatory(s.name, false)} disabled={toggling === s.name}>
                  {toggling === s.name ? '...' : '✕ Remover'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Adicionar skill obrigatória */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>
          Adicionar skill obrigatória
        </div>
        {availableToAdd.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
            Todas as skills já estão marcadas como obrigatórias
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {availableToAdd.map((s: any) => (
              <Button variant="ghost" onClick={() => toggleMandatory(s.name, true)} disabled={toggling === s.name}>
                {s.emoji || ''} {s.name} <span style={{ color: T.purpleBg, marginLeft: 4 }}>+</span>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const pipelinesData = signal<PipelineInfo[]>([]);
const pipelinesLoading = signal(false);
const pipelinesError = signal('');
const expandedPipeline = signal<string | null>(null);
const pipelineRuns = signal<PipelineRun[]>([]);
const pipelineRunsLoading = signal(false);
const dryRunResult = signal<any>(null);
const dryRunLoading = signal<string | null>(null);

async function fetchPipelines() {
  pipelinesLoading.value = true;
  pipelinesError.value = '';
  try {
    const d = await api.get<any>('/admin/pipelines');
    pipelinesData.value = d?.pipelines || [];
  } catch (e) {
    pipelinesError.value = (e as Error).message;
  }
  pipelinesLoading.value = false;
}

async function fetchPipelineRuns(pipelineId: string) {
  pipelineRunsLoading.value = true;
  try {
    const d = await api.get<any>(`/admin/pipelines/${pipelineId}/runs`);
    pipelineRuns.value = d?.runs || [];
  } catch { /* ignore */ }
  pipelineRunsLoading.value = false;
}

async function runDryRun(pipelineId: string) {
  dryRunLoading.value = pipelineId;
  dryRunResult.value = null;
  try {
    const d = await api.post<any>(`/admin/pipelines/${pipelineId}/test`);
    dryRunResult.value = d;
  } catch { /* ignore */ }
  dryRunLoading.value = null;
}

function PipelinesRegistryTab() {
  useEffect(() => { fetchPipelines(); }, []);

  if (pipelinesLoading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
  if (pipelinesError.value) return <div style={{ color: colors.red, padding: 16 }}>Erro: {pipelinesError.value}</div>;

  const pipelines = pipelinesData.value;
  const expanded = expandedPipeline.value;

  const stepColors: Record<string, string> = {
    'k8s-base': T.primary, configmap: T.purple, agents: T.warning, channels: T.success,
    skills: T.danger, brain: T.accent, secret: T.danger, 'init-db': T.warning,
    db: T.warning, 'env-vars': T.success, 'admin-setup': T.purple,
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>
        Pipeline Registry ({pipelines.length} pipelines)
      </h3>

      {/* Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 24 }}>
        {pipelines.map(p => {
          const isExpanded = expanded === p.id;
          return (
            <div key={p.id} style={{
              background: colors.card, borderRadius: 12, padding: 16,
              border: `1px solid ${isExpanded ? colors.blue : colors.cardBorder}`,
              cursor: 'pointer', transition: 'border-color 0.2s',
            }}
              onClick={() => {
                expandedPipeline.value = isExpanded ? null : p.id;
                if (!isExpanded) fetchPipelineRuns(p.id);
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>{p.name}</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  background: `${colors.blue}20`, color: colors.blue,
                }}>{p.id}</span>
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>{p.description}</div>

              <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                <div><span style={{ color: colors.textMuted }}>Steps:</span> <span style={{ color: colors.text, fontWeight: 600 }}>{p.steps.length}</span></div>
                <div><span style={{ color: colors.textMuted }}>Apps:</span> <span style={{ color: colors.text, fontWeight: 600 }}>{p.appsCount}</span></div>
                <div><span style={{ color: colors.textMuted }}>Health:</span> <span style={{ color: p.healthCheck ? colors.green : colors.textMuted, fontWeight: 600 }}>{p.healthCheck || 'none'}</span></div>
              </div>

              {/* Steps pills */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                {p.steps.map(s => (
                  <span key={s} style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                    background: `${stepColors[s] || T.textMuted}20`,
                    color: stepColors[s] || T.textMuted,
                    fontFamily: 'monospace',
                  }}>{s}</span>
                ))}
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{ marginTop: 16, borderTop: `1px solid ${colors.cardBorder}`, paddingTop: 12 }}>
                  {/* Apps list */}
                  {p.apps.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, marginBottom: 6 }}>Apps ({p.appsCount})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {p.apps.map(a => (
                          <span key={a.slug} style={{
                            padding: '2px 8px', borderRadius: 6, fontSize: 11,
                            background: T.border, color: colors.text,
                          }}>{a.name} <span style={{ color: colors.textMuted }}>({a.category})</span></span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dry Run button */}
                  <Button variant="ghost" onClick={(e) => { e.stopPropagation(); runDryRun(p.id); }} disabled={dryRunLoading.value === p.id}>
                    {dryRunLoading.value === p.id ? 'Simulando...' : '🧪 Dry Run'}
                  </Button>

                  {/* Dry run result */}
                  {dryRunResult.value && dryRunResult.value.pipelineId === p.id && (
                    <div style={{
                      background: T.purpleBg, borderRadius: 8, padding: 12, marginBottom: 12,
                      fontSize: 12, fontFamily: 'monospace',
                    }}>
                      <div style={{ fontWeight: 600, color: colors.purple, marginBottom: 6 }}>Dry Run Result</div>
                      {(dryRunResult.value.steps || []).map((s: any, i: number) => (
                        <div key={i} style={{ color: s.hasHandler ? colors.green : colors.textMuted, marginBottom: 2 }}>
                          {s.hasHandler ? '' : ''} {s.name} — {s.description}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recent runs */}
                  {pipelineRunsLoading.value ? (
                    <ListSkeleton rows={2} />
                  ) : pipelineRuns.value.length > 0 ? (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, marginBottom: 6 }}>Runs Recentes</div>
                      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {pipelineRuns.value.slice(0, 10).map(r => (
                          <div key={r.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '6px 8px', borderRadius: 6, marginBottom: 4,
                            background: T.bgCard, fontSize: 11,
                          }}>
                            <span style={{ color: colors.text, fontFamily: 'monospace' }}>{r.workspaceId.slice(0, 12)}...</span>
                            <span style={{
                              color: r.status === 'completed' ? colors.green : r.status === 'failed' ? colors.red : colors.yellow,
                              fontWeight: 600,
                            }}>{r.status}</span>
                            <span style={{ color: colors.textMuted }}>{r.durationMs ? `${(r.durationMs / 1000).toFixed(1)}s` : '-'}</span>
                            <span style={{ color: colors.textMuted }}>{new Date(r.startedAt).toLocaleString('pt-BR')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: colors.textMuted }}>Nenhum run registrado</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function btnStyle(bg: string): Record<string, string | number> {
  return {
    padding: '4px 12px',
    background: bg,
    color: T.text,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
  };
}
